import { baseConfig } from '@adornis/config/baseConfig.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { AddLeadsModal } from '../add-leads-modal.js';
import { filterLeadsBySearch, leadSearchSummary } from '../lead-search.js';
import { resolveLeadSendingAccount } from '../lead-sending-account.js';
import { buildCampaignLeadsCsv, campaignFilenameSlug, downloadTextFile } from '../lead-csv-export.js';
import { confirmDialog, confirmDialogWithCheckbox } from '../confirm-dialog.js';
import { dmWithheld, dmWithheldNotice } from '../already-connected-notice.js';
import { bounceReading } from '../measured-counters.js';
import { replyRate } from '../reply-rate.js';
import { emptyMessagesNotice } from '../lead-messages-notice.js';
import { chipPredicates, resolveLeadStateChip } from '../lead-state-chip.js';
import { ConnectionNoteNotice } from '../connection-note-notice.js';
import { connectStepCarriesANote } from '../wizard/types.js';
import { sdrRouter } from '../sdr-router.js';
import { skeletonLine, skeletonRows } from '../skeleton.js';
import { toast } from '../toast.js';

interface SequenceStep {
  stepId: string;
  channel: string;
  delayValue: number;
  delayUnit: string;
  condition: string;
  prompt: string;
  subject: string;
}

interface CampaignData {
  _id: string;
  name: string;
  /** Free-text note about this campaign — informational only, never sent or fed to the LLM. */
  description?: string;
  campaignType: string;
  status: string;
  externalCampaignId?: string;
  totalSent?: number;
  totalOpened?: number;
  /** Where `totalOpened` came from — see `measured-counters.ts`. Absent means nothing tracked it. */
  openSource?: string;
  totalReplied?: number;
  /** Leads contacted on any channel — computed by the server from the leads. */
  totalContacted?: number;
  totalBounced?: number;
  /** Where `totalBounced` came from — see `measured-counters.ts`. Absent means nothing has looked. */
  bounceSource?: string;
  totalConnectionsSent?: number;
  totalConnectionsAlreadyConnected?: number;
  /** Leads whose connect step was skipped because an invite was already outstanding — not connected. */
  totalInvitesPendingElsewhere?: number;
  totalAccepted?: number;
  totalDmSent?: number;
  totalRepliesSent?: number;
  totalDone?: number;
  leadsProcessed?: number;
  apolloLeadsFilteredNoEmail?: number;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
  launchedAt?: string;
  /** ISO timestamp — set while status is 'preparing_to_launch'; the launch fires once this passes. */
  scheduledLaunchAt?: string;
  linkedinAccountIds?: string[];
  sequence?: SequenceStep[];
  aiEmployeeId?: string;
  aiEmployeeAssignments?: { aiEmployeeId: string; percentage: number }[];
  offering?: {
    name?: string;
    description?: string;
    talkingPoints?: string[];
    sourceDocuments?: { fileName: string; uploadedAt?: string; url?: string; source?: string }[];
  };
  autoReply?: boolean;
  replyPrompt?: string;
  llmModel?: { llmId: string; model: string; label: string };
  replyLlmModel?: { llmId: string; model: string; label: string };
  generationProgress?: { total: number; done: number; failed: number };
  messagesGeneratedAt?: string;
  /** When true, CSV-origin leads are also pushed to the CRM on every sync tick. */
  crmAutoSync?: boolean;
  /** Serialised ApolloLeadSearchInput captured when this campaign was Apollo-sourced — presence enables the "top up from Apollo" option. */
  apolloSearchInputJson?: string;
  projectId?: string;
}

interface CampaignMessage {
  _id: string;
  leadId: string;
  stepId: string;
  stepIndex: number;
  channel: string;
  subject: string;
  body: string;
  originalSubject: string;
  originalBody: string;
  edited: boolean;
  editedAt?: string;
  generationStatus: 'pending' | 'generating' | 'ready' | 'failed' | 'deferred';
  generationError?: string;
  generatedAt?: string;
}

/**
 * Engagement buckets the leads table can be narrowed to. Each one corresponds
 * to a stat card above the table, so the headline numbers ARE the filter — the
 * pain this solves is having a "3 Replied" card and no way to find those 3
 * among 100 rows. `repliesSent` counts replies WE sent (a sent ReplyDraft),
 * not replies received.
 */
type LeadFilterKey =
  | 'all'
  | 'pending'
  | 'contacted'
  | 'connSent'
  | 'accepted'
  | 'alreadyConnected'
  | 'skippedAlreadyConnected'
  | 'invitePending'
  | 'inviteReceived'
  | 'inviteSentIgnored'
  | 'noInvite'
  | 'connectionUnknown'
  | 'waiting'
  | 'dmSent'
  | 'sent'
  | 'opened'
  | 'replied'
  | 'repliesSent'
  | 'bounced'
  | 'optedOut'
  | 'done';

type LeadSortKey = 'relevance' | 'touchedOldest' | 'touchedNewest' | 'nameAsc';

/** One lead inside a Compare-Leads split screen (mirrors the server's CompareLead). */
interface CompareLead {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  linkedinUrl?: string;
  status: string;
}

/** The duplicate overlap with ONE sibling campaign — one split screen per group. */
interface LeadConflictGroup {
  siblingCampaignId: string;
  siblingCampaignName: string;
  siblingCampaignStatus: string;
  pairs: { movedLead: CompareLead; siblingLead: CompareLead }[];
}

interface LeadData {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  linkedinUrl?: string;
  status: string;
  /** Operator's Manual Overwrite; wins the status chip when set. */
  manualStatus?: string;
  /** When set, the operator dismissed the "already connected" notice — keep it hidden. */
  alreadyConnectedNoticeDismissedAt?: string;
  currentStep: number;
  instantlyLeadStatus?: number;
  sentAt?: string;
  openedAt?: string;
  repliedAt?: string;
  completedAt?: string;
  bouncedAt?: string;
  replyText?: string;
  replyMessages?: { text: string; timestamp: string }[];
  lastContactedAt?: string;
  connectionSentAt?: string;
  /** They accepted OUR invite (positively confirmed). */
  acceptedAt?: string;
  /** They were already a 1st-degree connection — no invite was ever sent. Not an acceptance. */
  alreadyConnected?: string;
  /**
   * The authoritative connection state — 'connected' | 'invite_sent_pending' |
   * 'invite_received_pending' | 'not_connected_no_invite' | 'unknown'.
   * Everything above it is a projection of this.
   */
  connectionState?: string;
  /**
   * The server's decision about this lead's next step: what it is, whether it
   * can go now, and why not. Computed by the same function the send endpoint
   * obeys, so the button and the server cannot disagree.
   */
  nextStep?: {
    available: boolean;
    reason: string | null;
    /** Which kind of "no next step" this is — set exactly when `channel` is null. */
    noneBecause?: 'replied' | 'done' | 'all_sent' | 'no_steps' | null;
    stepNumber: number | null;
    channel: string | null;
    scheduledAt: string | null;
    waitingReason: string | null;
  };
  /**
   * A request to them was ALREADY outstanding, so none of ours went out. NOT a
   * connection: they simply have not acted on it, which is why no DM can be
   * delivered to them.
   */
  invitePendingElsewhere?: string;
  dmSentAt?: string;
  doneAt?: string;
  /**
   * On the global Do Not Contact list. Set by the lead-list route
   * (routes/campaign-leads.ts), which also forces `status` to 'opted_out' for
   * these — but `repliedAt` can still be present from an answer on this row.
   */
  suppressed?: boolean;
  /**
   * On the global pre-existing-connection list — the person was already a
   * connection (or already had an outstanding invitation) before any campaign.
   * Set by the lead-list route. Unlike `suppressed` it does NOT rewrite status;
   * it drives the "skipped — already connected" chip even for a pure email lead
   * that has no LinkedIn observation of its own.
   */
  knownConnection?: boolean;
  replyPromptOverride?: string;
  hasPendingDraft?: boolean;
  /** Sales Rep (AI Employee) assigned to this lead. */
  aiEmployeeId?: string;
  /** Set when the lead is linked to a CRM contact (e.g. HubSpot) — updates are pushed back. */
  crmContactId?: string;
  /** Origin of this lead row — 'csv' | 'hubspot' | etc. */
  source?: string;
}

interface SalesRepInfo {
  _id: string;
  name: string;
}

interface LeadEmail {
  type: string;
  subject: string;
  body: string;
  date: string;
  simulated?: boolean;
  channel?: string;
}

interface LinkedinJobRow {
  _id: string;
  leadId: string;
  channel: string;
  status: string;
  stepIndex: number;
  executedAt?: string;
  scheduledAt?: string;
  retries?: number;
  errorMessage?: string;
  /** The account this job sends from — a mailbox for an email job, a LinkedIn seat otherwise. */
  linkedinAccountId?: string;
}

interface DraftData {
  _id: string;
  leadId: string;
  channel: string;
  stepIndex: number;
  customerReply: string;
  classifyResult: {
    replyIntent: string;
    sentiment: string;
    promptActionAppropriate: boolean;
    reasoning: string;
    suggestedStrategy: string;
  };
  conversationHistory: { role: string; message: string; timestamp: string }[];
  draftMessage: string;
  draftSubject?: string;
  editedMessage?: string;
  status: string;
  generatedAt: string;
  reviewedAt?: string;
  leadName: string;
  leadCompany: string;
  leadEmail: string;
}

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * A future instant as a short, readable local time — "Tue, 09:00" if it's on a
 * different day, "09:00" if it's still today. Used for "sending again …" lines.
 */
function formatWhen(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const sameDay = d.toDateString() === new Date().toDateString();
  if (sameDay) return time;
  const day = d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
  return `${day}, ${time}`;
}

/** Formats milliseconds remaining as "H:MM:SS" (or "MM:SS" once under an hour). */
function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function abbreviateUrl(url: string, maxLength = 30): string {
  const clean = url.replace(/^https?:\/\/(www\.)?/, '');
  if (clean.length <= maxLength) return clean;
  return clean.slice(0, maxLength - 1) + '…';
}

/**
 * Readable text out of an email reply that was stored as the provider's HTML.
 * The backend cleans new replies at capture; this is the safety-net for replies
 * stored before that, so the raw markup never reaches the panel.
 */
function replyDisplayText(raw: string | undefined | null): string {
  if (!raw) return '';
  return raw
    .replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, ' ')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(?:div|p|blockquote|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * What a job's step is called on screen.
 *
 * Every channel but `linkedin_dm` used to render as "Connect". So an EMAIL
 * campaign whose mailbox sends were being refused listed them as `Connect`
 * steps, next to a verbatim Unipile *email* 422 — reported on 17 August with
 * the reasonable question of how LinkedIn had got into an email campaign. A
 * label that is wrong about the channel makes the operator distrust the rest of
 * the row, which is the row that was telling the truth.
 *
 * An unrecognised channel returns its own name rather than a guess: a new
 * channel should look unfamiliar, not like a Connect step.
 */
function stepLabelFor(channel: string): string {
  switch (channel) {
    case 'linkedin_connect':
      return 'Connect';
    case 'linkedin_dm':
      return 'DM';
    case 'email':
      return 'Email';
    case 'email_followup':
      return 'Email follow-up';
    default:
      return channel;
  }
}

/** True when running on staging deployment where real emails/LinkedIn sends occur. */
const isStaging = (): boolean => baseConfig.get('DEPLOYMENT') === 'staging';

/**
 * How often the campaign-detail page silently auto-syncs while the campaign is
 * active and the tab is visible. Runs in addition to the server-side
 * sync-tick-worker (which keeps campaigns fresh when no tab is open) so the user
 * never has to click "Sync" to see new sends / replies land.
 */
const AUTO_SYNC_INTERVAL_MS = 60_000;

// Per-campaign, per-browser dismissal of the "sourced Apollo leads were skipped"
// notice. This is a view convenience, not campaign state — the skip already
// happened and its count is fixed on the campaign — so it lives in localStorage
// rather than the database: once the operator clicks Okay, the notice stays gone
// for THIS campaign in THIS browser, and never comes back. Every access is
// guarded, so a browser that refuses storage (private window) simply shows the
// notice rather than throwing.
const apolloNoticeDismissKey = (campaignId: string) => `campaign:${campaignId}:apolloSkipNoticeDismissed`;
function readApolloNoticeDismissed(campaignId: string): boolean {
  try {
    return localStorage.getItem(apolloNoticeDismissKey(campaignId)) === '1';
  } catch {
    return false;
  }
}
function writeApolloNoticeDismissed(campaignId: string): void {
  try {
    localStorage.setItem(apolloNoticeDismissKey(campaignId), '1');
  } catch {
    // A browser that refuses storage just shows the notice again next time —
    // not worth surfacing an error for a dismissible hint.
  }
}

export const CampaignDetail = stateful(({ campaignId }: { campaignId: string }) => {
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [leads, setLeads] = useState<LeadData[]>([]);
  // Whether the operator has already dismissed the "sourced Apollo leads were
  // skipped" notice for this campaign. Initialised from localStorage (campaignId
  // is a prop, so it is known on the first render) so a dismissal survives a
  // reload and never comes back for this campaign.
  const [apolloNoticeDismissed, setApolloNoticeDismissed] = useState<boolean>(
    readApolloNoticeDismissed(campaignId),
  );
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // Stable mutable guard (functional-lit has no useRef) so the manual Sync
  // button and the auto-sync interval never fire overlapping sync requests.
  const [syncGuard] = useState<{ inFlight: boolean }>({ inFlight: false });
  const [actionLoading, setActionLoading] = useState('');
  const [projects, setProjects] = useState<{ _id: string; name: string; isDefault?: boolean }[]>([]);
  const [movingProject, setMovingProject] = useState(false);
  // Set when a move is blocked by duplicate leads — shows a confirmation
  // dialog listing them with a "Remove" action that retries the move after
  // deleting (or, if the campaign is active and can't safely delete leads,
  // pausing it instead — the server decides which is safe).
  const [moveConflict, setMoveConflict] = useState<{
    projectId: string;
    leads: { _id: string; firstName: string; lastName: string; email: string; linkedinUrl?: string }[];
  } | null>(null);
  // Compare-Leads session: both campaigns are paused server-side and the user
  // resolves one campaign pair at a time in a split screen. `pendingRemovals`
  // accumulates the lead ids picked across ALL pairs — nothing is deleted
  // until Save, so switching pairs or cancelling never loses/destroys data.
  const [compareSession, setCompareSession] = useState<{
    projectId: string;
    projectName: string;
    movedCampaignName: string;
    groups: LeadConflictGroup[];
  } | null>(null);
  const [comparePairIndex, setComparePairIndex] = useState(0);
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set());
  const [compareBusy, setCompareBusy] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  // Inline edit of the campaign's own name + description (the free-text note,
  // not the Offering — that drives message generation and lives in the wizard).
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaName, setMetaName] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  // Which engagement bucket the leads table is narrowed to. Driven by clicking
  // the stat cards above it, so the numbers double as the filter control.
  const [leadFilter, setLeadFilter] = useState<LeadFilterKey>('all');
  const [leadSort, setLeadSort] = useState<LeadSortKey>('relevance');
  // G2/G3 lead search — a case-insensitive substring over name, company and
  // email of the loaded leads. Narrows the table alongside the status filter.
  const [leadSearch, setLeadSearch] = useState('');
  /** Lead whose next step is currently being queued by the "Send Now" button. */
  const [sendingNextStepLeadId, setSendingNextStepLeadId] = useState<string | null>(null);
  const [savingStatusLeadId, setSavingStatusLeadId] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState('');
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
  const [leadEmails, setLeadEmails] = useState<LeadEmail[]>([]);
  const [instantlyInvolved, setInstantlyInvolved] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [drafts, setDrafts] = useState<DraftData[]>([]);
  const [, setLoadingDrafts] = useState(false);
  const [approvingDraftId, setApprovingDraftId] = useState<string | null>(null);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [editDraftMessage, setEditDraftMessage] = useState('');
  const [expandedDraftId, setExpandedDraftId] = useState<string | null>(null);
  // Which draft has its surrounding context (reasoning, conversation, the lead's
  // reply) expanded. Collapsed by default: the reply itself leads, context is a
  // click away.
  const [expandedContextDraftId, setExpandedContextDraftId] = useState<string | null>(null);
  const [replyPromptText, setReplyPromptText] = useState('');
  const [savingReplyPrompt, setSavingReplyPrompt] = useState(false);
  const [editingReplySettings, setEditingReplySettings] = useState(false);
  const [replyToAllResult, setReplyToAllResult] = useState<string | null>(null);
  const [replyToAllSendLoading, setReplyToAllSendLoading] = useState(false);
  // Pre-launch messages (review/edit before campaign launch)
  const [messages, setMessages] = useState<CampaignMessage[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editSubjectDraft, setEditSubjectDraft] = useState('');
  const [editBodyDraft, setEditBodyDraft] = useState('');
  const [savingMessageId, setSavingMessageId] = useState<string | null>(null);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);
  const [deletingLeadId, setDeletingLeadId] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);
  // "Add Leads" modal (shared AddLeadsModal component). Only opened for
  // paused campaigns — pre-launch lead management lives in the wizard's
  // Leads step, which isn't reachable while paused.
  const [addLeadsOpen, setAddLeadsOpen] = useState(false);
  // Per-lead expand state for the "Generated Messages" section (post-launch it is collapsed by default)
  const [expandedMessagesLeadIds, setExpandedMessagesLeadIds] = useState<Set<string>>(new Set());
  // Whether the "Offering" overview section is expanded (collapsed by default)
  const [offeringExpanded, setOfferingExpanded] = useState(false);
  // Lookup map: aiEmployeeId → display name. Loaded once per page mount so the
  // lead detail panel can show "Sent by <Name>" instead of an opaque id.
  const [salesRepsById, setSalesRepsById] = useState<Record<string, string>>({});
  // LinkedIn job health — surfaced as a banner when jobs repeatedly fail so the
  // user doesn't wait days before noticing something is broken upstream.
  const [linkedinHealth, setLinkedinHealth] = useState<{
    hasIssues: boolean;
    failedCount: number;
    retryingCount: number;
    /** Parked by a LinkedIn rate limit — the safety limit working, not a fault. */
    throttledCount: number;
    /**
     * What is actually wrong, grouped by the status the provider returned AND
     * by which provider it was — `advice` names one, and naming the wrong one
     * sends the operator to check an account that is not involved.
     */
    kinds: { kind: string; channel: string; count: number; advice: string; resolvesItself: boolean }[];
    sampleError: string | null;
  } | null>(null);
  // The stuck-job detail, fetched only when asked for. The banner stays a
  // summary; the provider's own words belong behind a deliberate click, not in
  // a passive alert where they read as a broken product.
  const [stuckJobs, setStuckJobs] = useState<
    | {
        lead: string | null;
        channel: string;
        status: string;
        retries: number;
        failureKind: string;
        failureStatus: number | null;
        errorMessage: string | null;
      }[]
    | null
  >(null);
  const [loadingStuck, setLoadingStuck] = useState(false);
  const showStuckDetails = async () => {
    setLoadingStuck(true);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/stuck-jobs`);
      const data = await resp.json();
      setStuckJobs(data?.ok ? (data.jobs ?? []) : []);
    } catch {
      setStuckJobs([]);
    } finally {
      setLoadingStuck(false);
    }
  };
  // Per-lead LinkedIn job rows (campaign-wide; filtered by leadId at render time).
  // The /linkedin-jobs endpoint already returns the full job list — we keep it so
  // the per-lead engagement boxes can show exact per-step send dates.
  const [linkedinJobs, setLinkedinJobs] = useState<LinkedinJobRow[]>([]);
  // Per sending account: what state it is in, and the numbers behind it. The
  // account is the thing the provider punishes, and until now the only place it
  // appeared on screen was as a setting in the wizard.
  const [accountStates, setAccountStates] = useState<
    {
      linkedinAccountId: string;
      invitesSent: number;
      invitesRefused: number;
      invitesAttempted: number;
      connectionRequestLimit: number;
      warmupMultiplier: number;
      remaining: number;
      health: string;
      healthReasons: string[];
      needsAttention: boolean;
    }[]
  >([]);
  // Provider account id → the name it shows in Settings. Lets a per-lead label
  // and the account-state panel say "via Anna Weber" instead of a raw seat id.
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});
  // Why the campaign isn't sending right now, if it isn't: a closed sending
  // window and/or accounts that have hit a limit — with WHEN each resumes.
  // Feeds the two "informed, not silently blocked" banners (UAT F2).
  const [sendingStatus, setSendingStatus] = useState<{
    window: { open: boolean; nextOpenAt: string | null };
    limits: { channel: string; accountId: string; reason: string; nextSendAt: string | null }[];
  } | null>(null);

  const fetchMessages = async () => {
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/messages`);
      const data = await resp.json();
      if (data.ok) setMessages(data.messages ?? []);
    } catch {
      /* ignore */
    }
  };

  const fetchData = async () => {
    try {
      const [cResp, lResp, mResp, jResp, sResp] = await Promise.all([
        fetch(`/api/campaigns/${encodeURIComponent(campaignId)}`),
        fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/leads`),
        fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/messages`),
        fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/linkedin-jobs`),
        fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/sending-status`),
      ]);
      const cData = await cResp.json();
      const lData = await lResp.json();
      const mData = await mResp.json();
      const jData = await jResp.json().catch(() => null);
      const sData = await sResp.json().catch(() => null);
      if (cData.ok) {
        setCampaign(cData.campaign);
        setReplyPromptText(cData.campaign.replyPrompt ?? '');
      }
      if (lData.ok) {
        setLeads(lData.leads ?? []);
      }
      if (mData.ok) setMessages(mData.messages ?? []);
      if (jData?.ok && jData.health) setLinkedinHealth(jData.health);
      if (jData?.ok && Array.isArray(jData.jobs)) setLinkedinJobs(jData.jobs);
      if (jData?.ok && Array.isArray(jData.budget)) setAccountStates(jData.budget);
      if (jData?.ok && jData.accountNames) setAccountNames(jData.accountNames);
      if (sData?.ok && sData.window) {
        setSendingStatus({ window: sData.window, limits: Array.isArray(sData.limits) ? sData.limits : [] });
        // Names come from the same resolver as the LinkedIn view (all providers,
        // mailboxes included), so accountNames is already covered above.
      }
    } catch {
      setError('Failed to load campaign data');
      toast.error('Failed to load campaign data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/projects');
        const data = await resp.json();
        if (data.ok) setProjects(data.projects);
      } catch {
        /* ignore — project picker just stays empty */
      }
    })();
  }, []);

  const moveToProject = async (projectId: string, removeDuplicateLeadIds?: string[]) => {
    if (!campaign) return;
    if (!removeDuplicateLeadIds && projectId === (campaign.projectId ?? projects.find(p => p.isDefault)?._id)) return;
    setMovingProject(true);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/project`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, removeDuplicateLeadIds }),
      });
      const data = await resp.json();
      if (!data.ok) {
        if (data.error === 'duplicate_leads') {
          setMoveConflict({ projectId, leads: data.conflictingLeads ?? [] });
          return;
        }
        toast.error(data.error ?? 'Failed to move campaign');
        return;
      }
      setMoveConflict(null);
      await fetchData();
      if (data.pausedDueToDuplicates) {
        toast.warning(
          "Some leads couldn't be safely removed from this active campaign — it was paused instead so the move could go through.",
        );
      } else {
        toast.success('Campaign moved.');
      }
    } catch {
      toast.error('Failed to move campaign (network error)');
    }
    setMovingProject(false);
  };

  // ── Campaign name / description inline edit ──────────────────────────────
  const startEditMeta = () => {
    setMetaName(campaign?.name ?? '');
    setMetaDescription(campaign?.description ?? '');
    setEditingMeta(true);
  };
  const saveMeta = async () => {
    if (!metaName.trim()) {
      toast.error('Campaign name cannot be empty');
      return;
    }
    setSavingMeta(true);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: metaName.trim(), description: metaDescription.trim() }),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to save');
        return;
      }
      setEditingMeta(false);
      await fetchData();
      toast.success('Campaign details saved.');
    } catch {
      toast.error('Failed to save (network error)');
    }
    setSavingMeta(false);
  };

  // ── "Send Now": bring a lead's next step forward ─────────────────────────
  /**
   * Why the next step can't be sent right now, or null when it can. Mirrors the
   * server's own gate (see POST .../send-next-step) so the button explains
   * itself instead of failing on click — a DM needs a real connection, and a
   * merely SENT connection request is not one.
   */
  /**
   * Why the next step cannot be sent, or null when it can.
   *
   * Read from the server, never recomputed here. This used to derive the next
   * step from `currentStep` in the sequence while the server derived it from
   * the job list, and for a lead whose connection request had been skipped the
   * two disagreed: the button offered the DM, found no connection, and refused
   * — advising the operator to send the very connection request the server
   * would have sent if asked.
   */
  const sendNowBlockedReason = (lead: LeadData): string | null => {
    if (!lead.nextStep) return 'Checking…';
    return lead.nextStep.available ? null : (lead.nextStep.reason ?? 'This step cannot be sent right now');
  };

  const sendNextStepNow = async (lead: LeadData) => {
    setSendingNextStepLeadId(lead._id);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/leads/${encodeURIComponent(lead._id)}/send-next-step`,
        { method: 'POST' },
      );
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Could not send the next step');
        return;
      }
      // Deliberately "sending", not "sent": the worker picks the job up within
      // a minute and the counters move when the send actually happens.
      toast.success(data.message ?? 'Queued for immediate sending.');
      await fetchData();
    } catch {
      toast.error('Could not send the next step (network error)');
    }
    setSendingNextStepLeadId(null);
  };

  // The manual-overwrite vocabulary, shared by the row chip menu and the
  // expanded-detail control. '' is the "hand it back to the automation" option.
  const MANUAL_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: '', label: 'Automatic' },
    { value: 'pending', label: 'Pending' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'replied', label: 'Replied' },
    { value: 'bounced', label: 'Bounced' },
    { value: 'opted_out', label: 'Opted out' },
  ];
  const manualStatusLabel = (value: string): string =>
    MANUAL_STATUS_OPTIONS.find(o => o.value === value)?.label ?? value;

  // Manual status overwrite: set (or clear, with '') a lead's status by hand.
  // A terminal status also stops the lead's remaining outreach server-side.
  //
  // An active status (pending/contacted) does real work when the lead is not just
  // running:
  //  • on a Do-Not-Contact lead it REACTIVATES them — off the global list,
  //    outreach resumes (reverses an opt-out, applies to every campaign holding
  //    the email); confirmed, then `reactivate: true`.
  //  • on a FINISHED lead it RESUMES them from the next unsent step (already-sent
  //    steps are never repeated); confirmed, then `resume: true`.
  //  • on a still-running lead it is just the shown label.
  // Hide the "already connected — no DM was sent" notice on this lead, for good.
  // Persisted server-side (alreadyConnectedNoticeDismissedAt), so it stays gone
  // across reloads. Purely a display acknowledgement — it does not un-mark the
  // lead or change any outreach.
  const dismissConnectedNotice = async (lead: LeadData) => {
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/leads/${encodeURIComponent(lead._id)}/dismiss-connected-notice`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      );
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Could not dismiss the notice');
        return;
      }
      await fetchData();
    } catch {
      toast.error('Could not dismiss the notice (network error)');
    }
  };

  const setLeadStatus = async (lead: LeadData, status: string) => {
    const isActive = status === 'pending' || status === 'contacted';
    // Re-opening a lead (reactivate a Do-Not-Contact one, or resume a finished
    // one) regenerates its messages, which only happens when the campaign is
    // continued out of its edit window. So it can't be done mid-send — pause the
    // campaign first, exactly as adding a Sales Rep is paused-only.
    const wouldReopen = isActive && (!!lead.suppressed || !!lead.doneAt);
    if (wouldReopen && campaign?.status === 'active') {
      toast.error(
        'Pause the campaign first — moving this lead back to Pending/Contacted regenerates its messages, ' +
          'which happens when you continue.',
      );
      return;
    }
    let reactivate = false;
    let resume = false;
    if (isActive && lead.suppressed) {
      const ok = await confirmDialog(
        `${lead.firstName} ${lead.lastName} is on the global Do Not Contact list. ` +
          `Setting “${manualStatusLabel(status)}” takes them off that list and lets outreach resume — ` +
          `for every campaign that holds this email, reversing their opt-out.`,
        {
          title: 'Reactivate this contact?',
          confirmLabel: 'Reactivate',
          cancelLabel: 'Keep on Do Not Contact',
          confirmClass: 'btn-warning',
        },
      );
      if (!ok) return;
      reactivate = true;
    } else if (isActive && lead.doneAt) {
      const ok = await confirmDialog(
        `${lead.firstName} ${lead.lastName} has finished — no more steps are scheduled. ` +
          `Setting “${manualStatusLabel(status)}” resumes outreach from their next unsent step. ` +
          `Already-sent steps are never repeated.`,
        {
          title: 'Resume this lead?',
          confirmLabel: 'Resume',
          cancelLabel: 'Cancel',
          confirmClass: 'btn-warning',
        },
      );
      if (!ok) return;
      resume = true;
    }

    setSavingStatusLeadId(lead._id);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/leads/${encodeURIComponent(lead._id)}/status`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, reactivate, resume }),
        },
      );
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Could not update the status');
        return;
      }
      // Server safety nets: the lead needed an explicit flag we did not send, or
      // the campaign must be paused first. Never claim success on any of them.
      if (data.requiresPause) {
        toast.error(data.error ?? 'Pause the campaign first to change this lead’s status.');
        return;
      }
      if (data.requiresReactivation) {
        toast.error('This contact is on the Do Not Contact list — reactivate them to change the status.');
        return;
      }
      if (data.requiresResume) {
        toast.error('This lead has finished — confirm to resume it from its next step.');
        return;
      }
      const jobs = (data.requeuedJobs as number) ?? 0;
      const tail = jobs > 0 ? ` (${jobs} step${jobs === 1 ? '' : 's'} re-queued).` : '.';
      const regen = (data.regeneratedMessages as number) ?? 0;
      // A re-opened lead only starts sending once the campaign is continued out of
      // its pause and the cool-down is skipped — that pass generates the queued
      // messages and creates the jobs. Point the operator at that next step.
      const genNote = regen > 0 ? ` ${regen} message${regen === 1 ? '' : 's'} queued for generation.` : '';
      const continueNote =
        data.reactivated || data.resumed
          ? ' Continue the campaign and skip the cool-down to generate and resume sending.'
          : '';
      let message: string;
      if (status === '') {
        message = 'Status override cleared.';
      } else if (data.reactivated) {
        message = `Reactivated — “${manualStatusLabel(status)}”.${genNote}${continueNote}`;
      } else if (data.resumed) {
        message = `Resumed — “${manualStatusLabel(status)}” continues from the next unsent step${tail}${genNote}${continueNote}`;
      } else if (resume) {
        // We asked to resume, but the server found nothing to re-open: every step
        // was already sent, so this is a label on a genuinely finished lead.
        message = `Set to “${manualStatusLabel(status)}”. Every step had already been sent — nothing to resume.`;
      } else if (data.stoppedOutreach) {
        message = `Status set to “${manualStatusLabel(status)}” — remaining outreach stopped.`;
      } else {
        message = `Status set to “${manualStatusLabel(status)}”.`;
      }
      toast.success(message);
      await fetchData();
    } catch {
      toast.error('Could not update the status (network error)');
    }
    setSavingStatusLeadId(null);
  };

  // ── Compare Leads session ────────────────────────────────────────────────
  /** Opens the session: pauses both sides server-side and loads the per-pair conflict groups. */
  const openCompareLeads = async (projectId: string) => {
    setCompareBusy(true);
    setCompareError(null);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/project/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to open lead comparison');
        return;
      }
      if ((data.groups ?? []).length === 0) {
        // Nothing left to compare (e.g. the sibling was paused meanwhile) —
        // just do the plain move.
        setMoveConflict(null);
        await moveToProject(projectId);
        return;
      }
      setMoveConflict(null);
      setPendingRemovals(new Set());
      setComparePairIndex(0);
      setCompareSession({
        projectId,
        projectName: data.projectName ?? '',
        movedCampaignName: data.movedCampaignName ?? '',
        groups: data.groups,
      });
      await fetchData(); // reflect the pause that just happened
    } catch {
      toast.error('Failed to open lead comparison (network error)');
    }
    setCompareBusy(false);
  };

  /** Abandons the session — resumes whatever was paused, deletes nothing. */
  const cancelCompareLeads = async () => {
    if (!compareSession) return;
    setCompareBusy(true);
    try {
      await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/project/compare/cancel`, { method: 'POST' });
      setCompareSession(null);
      setPendingRemovals(new Set());
      await fetchData();
      toast.info('Comparison cancelled — no leads were removed.');
    } catch {
      toast.error('Failed to cancel comparison (network error)');
    }
    setCompareBusy(false);
  };

  /** Applies the picks: deletes them, verifies, moves, resumes both campaigns. */
  const saveCompareLeads = async () => {
    if (!compareSession) return;
    setCompareBusy(true);
    setCompareError(null);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/project/compare/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: compareSession.projectId, removeLeadIds: [...pendingRemovals] }),
      });
      const data = await resp.json();
      if (!data.ok) {
        if (data.error === 'duplicate_leads_remaining') {
          // Server refused: still duplicates somewhere. Re-seed the session
          // with what's actually left so the user sees the real remainder.
          setCompareSession({ ...compareSession, groups: data.groups ?? [] });
          setComparePairIndex(0);
          setPendingRemovals(new Set());
          setCompareError(data.message ?? 'Some leads are still in both campaigns.');
          await fetchData();
          return;
        }
        toast.error(data.error ?? 'Failed to save');
        return;
      }
      setCompareSession(null);
      setPendingRemovals(new Set());
      await fetchData();
      const resumedCount = (data.resumedCampaignIds ?? []).length;
      toast.success(
        `Campaign moved${data.removedLeads ? ` — ${data.removedLeads} duplicate lead${data.removedLeads === 1 ? '' : 's'} removed` : ''}.` +
          (resumedCount > 0 ? ` ${resumedCount} campaign${resumedCount === 1 ? '' : 's'} back in cool-down.` : ''),
      );
    } catch {
      toast.error('Failed to save (network error)');
    }
    setCompareBusy(false);
  };

  const toggleRemoval = (leadId: string) => {
    const next = new Set(pendingRemovals);
    if (next.has(leadId)) next.delete(leadId);
    else next.add(leadId);
    setPendingRemovals(next);
  };

  /**
   * A pair counts as resolved once at least one of its two records is marked
   * for removal — that's exactly what makes the duplicate go away. Drives both
   * the per-pair progress hint and the Save button's enabled state.
   */
  const pairResolved = (pair: { movedLead: CompareLead; siblingLead: CompareLead }): boolean =>
    pendingRemovals.has(pair.movedLead._id) || pendingRemovals.has(pair.siblingLead._id);
  const allPairsResolved = (compareSession?.groups ?? []).every(g => g.pairs.every(pairResolved));
  const unresolvedCount = (compareSession?.groups ?? []).reduce(
    (n, g) => n + g.pairs.filter(p => !pairResolved(p)).length,
    0,
  );

  // Load Sales Rep names once so we can resolve aiEmployeeId → human-readable
  // name in the lead detail panel. Failure is non-fatal (we just fall back to
  // showing the raw id).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/ai-employees');
        const data = (await resp.json()) as { ok: boolean; aiEmployees?: SalesRepInfo[] };
        if (cancelled || !data.ok) return;
        const map: Record<string, string> = {};
        for (const e of data.aiEmployees ?? []) map[e._id] = e.name;
        setSalesRepsById(map);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll while messages are being generated (status === 'generating')
  useEffect(() => {
    if (campaign?.status !== 'generating') return;
    const id = setInterval(() => {
      fetchData();
    }, 3000);
    return () => clearInterval(id);
  }, [campaign?.status, campaignId]);

  // Refetch immediately when the tab becomes visible again. Message
  // generation itself runs entirely server-side (a background loop in the
  // Node process) and is completely unaffected by the browser tab's
  // visibility — but browsers throttle setInterval in hidden tabs, so the
  // poll above can sit stale for a while after switching back, making it
  // LOOK like generation stopped even though it never did. This closes that
  // gap by forcing a fresh fetch the moment the tab regains focus, regardless
  // of campaign status (a plain DB refetch is cheap — unlike the Instantly/
  // Unipile auto-sync below, which deliberately stays paused while hidden).
  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) void fetchData();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [campaignId]);

  // Live countdown for the launch cool-down, ticking every second from
  // campaign.scheduledLaunchAt. Also periodically refetches so the page picks
  // up the transition to 'active' once the launch-cooldown worker actually
  // fires — the countdown hitting 0:00 client-side doesn't mean the server
  // has executed the launch yet (worker runs on its own ~60s tick).
  useEffect(() => {
    if (campaign?.status !== 'preparing_to_launch' || !campaign.scheduledLaunchAt) return;
    const scheduledAt = campaign.scheduledLaunchAt;
    const tick = () => setCooldownRemainingMs(Math.max(0, new Date(scheduledAt).getTime() - Date.now()));
    tick();
    const tickId = setInterval(tick, 1000);
    const refetchId = setInterval(() => void fetchData(), 10_000);
    return () => {
      clearInterval(tickId);
      clearInterval(refetchId);
    };
  }, [campaign?.status, campaign?.scheduledLaunchAt]);

  // Auto-sync: keep an active campaign live without manual "Sync" clicks. While
  // the campaign is active and the tab is visible, silently pull fresh
  // stats/replies from Instantly/Unipile on an interval (no toast/spinner).
  // Skipped when the tab is hidden to avoid wasteful API calls / rate limits;
  // the server-side sync-tick-worker covers campaigns when no tab is open.
  useEffect(() => {
    if (campaign?.status !== 'active') return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void syncCampaign({ silent: true });
    }, AUTO_SYNC_INTERVAL_MS);
    return () => clearInterval(id);
  }, [campaign?.status, campaignId]);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/drafts`);
      const data = await resp.json();
      if (data.ok) setDrafts(data.drafts ?? []);
    } catch {
      /* ignore */
    }
    setLoadingDrafts(false);
  };

  useEffect(() => {
    fetchDrafts();
  }, [campaignId]);

  const approveDraft = async (draftId: string, channel?: 'email' | 'linkedin_dm') => {
    setApprovingDraftId(draftId);
    try {
      const resp = await fetch(`/api/drafts/${encodeURIComponent(draftId)}/approve`, {
        method: 'PUT',
        headers: channel ? { 'Content-Type': 'application/json' } : undefined,
        body: channel ? JSON.stringify({ channel }) : undefined,
      });
      const data = await resp.json();
      if (data.ok) await fetchDrafts();
    } catch {
      /* ignore */
    }
    setApprovingDraftId(null);
  };

  const rejectDraft = async (draftId: string) => {
    try {
      await fetch(`/api/drafts/${encodeURIComponent(draftId)}/reject`, { method: 'PUT' });
      await fetchDrafts();
    } catch {
      /* ignore */
    }
  };

  const saveDraftEdit = async (draftId: string) => {
    try {
      await fetch(`/api/drafts/${encodeURIComponent(draftId)}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: editDraftMessage }),
      });
      setEditingDraftId(null);
      await fetchDrafts();
    } catch {
      /* ignore */
    }
  };

  const regenerateDraft = async (draftId: string) => {
    setApprovingDraftId(draftId);
    try {
      await fetch(`/api/drafts/${encodeURIComponent(draftId)}/regenerate`, { method: 'POST' });
      await fetchDrafts();
    } catch {
      /* ignore */
    }
    setApprovingDraftId(null);
  };

  const saveReplyPrompt = async () => {
    setSavingReplyPrompt(true);
    try {
      // Reply model is auto-selected on the server; UI only sends the prompt.
      await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/reply-prompt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ replyPrompt: replyPromptText }),
      });
      if (campaign) setCampaign({ ...campaign, replyPrompt: replyPromptText });
    } catch {
      /* ignore */
    }
    setSavingReplyPrompt(false);
  };

  const replyToAllAndSend = async () => {
    const confirmed = await confirmDialog(
      `This will send all existing pending drafts and generate + send new drafts for remaining replied leads. Continue?`,
      { title: 'Reply to All & Send', confirmLabel: 'Generate & Send', confirmClass: 'btn-accent' },
    );
    if (!confirmed) return;

    setReplyToAllSendLoading(true);
    setReplyToAllResult(null);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/reply-to-all-and-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await resp.json();
      if (data.ok) {
        const totalProcessed = (data.sentExisting ?? 0) + (data.queuedExisting ?? 0) + (data.generated ?? 0);
        if (totalProcessed === 0 && (data.total ?? 0) === 0) {
          setReplyToAllResult('No replied leads to process');
        } else {
          const parts: string[] = [];
          // Phase 1: existing drafts that were sent
          const existingSent = (data.sentExisting ?? 0) + (data.queuedExisting ?? 0);
          if (existingSent > 0) {
            const existParts: string[] = [];
            if (data.sentExisting > 0) existParts.push(`${data.sentExisting} sent`);
            if (data.queuedExisting > 0) existParts.push(`${data.queuedExisting} queued`);
            parts.push(`${existingSent} existing draft${existingSent > 1 ? 's' : ''}: ${existParts.join(', ')}`);
          }
          // Phase 2: newly generated drafts
          if (data.generated > 0) {
            const newParts: string[] = [];
            if (data.sent > 0) newParts.push(`${data.sent} sent`);
            if (data.queued > 0) newParts.push(`${data.queued} queued`);
            parts.push(`${data.generated} new draft${data.generated > 1 ? 's' : ''}: ${newParts.join(', ')}`);
          }
          if (data.errors?.length > 0) parts.push(`${data.errors.length} failed`);
          setReplyToAllResult(parts.join(' · '));
          await fetchDrafts();
        }
      } else {
        setReplyToAllResult(data.error ?? 'Failed');
      }
    } catch {
      setReplyToAllResult('Request failed');
    }
    setReplyToAllSendLoading(false);
  };

  const toggleLead = async (lead: LeadData) => {
    if (expandedLeadId === lead._id) {
      setExpandedLeadId(null);
      setLeadEmails([]);
      return;
    }
    setExpandedLeadId(lead._id);
    setLeadEmails([]);
    setLoadingEmails(true);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/leads/${encodeURIComponent(lead._id)}/emails`,
      );
      const data = await resp.json();
      if (data.ok) {
        setLeadEmails(data.emails ?? []);
        // Whether Instantly is involved at all decides which sentence an empty
        // list deserves. On the armed scheduler there is no provider reporting
        // delay to excuse — nothing sent means nothing sent.
        setInstantlyInvolved(data.instantlyInvolved === true);
      }
    } catch {
      /* ignore */
    }
    setLoadingEmails(false);
  };

  const syncCampaign = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    // Never stack syncs — a slow run must not be overlapped by the interval or
    // a second button click.
    if (syncGuard.inFlight) return;
    syncGuard.inFlight = true;
    if (!silent) {
      setSyncing(true);
      setError('');
    }
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/sync`, { method: 'POST' });
      const data = await resp.json();
      if (data.ok) {
        setCampaign(data.campaign);
        const lResp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/leads`);
        const lData = await lResp.json();
        if (lData.ok) {
          setLeads(lData.leads ?? []);
        }
        // The sync run can produce fresh AI-generated reply drafts (step (4)
        // of `syncCampaignInternal`). Without an explicit refetch the
        // ReplyDraft list here would only reflect what was visible *before*
        // the sync, so newly-generated drafts would appear to lag behind by
        // a full sync-tick interval. Refetching synchronously closes that
        // window so the user sees drafts as soon as the sync finishes.
        await fetchDrafts();
        if (!silent) toast.success('Campaign stats synced.');
      } else if (!silent) {
        setError(data.error || 'Sync failed');
        toast.error(data.error || 'Sync failed');
      }
    } catch {
      // Auto-sync failures stay silent (transient network blips shouldn't spam
      // toasts); only a user-initiated sync surfaces the error.
      if (!silent) {
        setError('Sync request failed');
        toast.error('Sync request failed — check your connection.');
      }
    } finally {
      syncGuard.inFlight = false;
      if (!silent) setSyncing(false);
    }
  };

  const performAction = async (action: 'pause' | 'resume' | 'archive' | 'unarchive') => {
    setActionLoading(action);
    setError('');
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/${action}`, { method: 'POST' });
      const data = await resp.json();
      if (data.ok) {
        setCampaign(data.campaign);
        const past =
          action === 'pause'
            ? 'paused'
            : action === 'resume'
              ? 'resumed'
              : action === 'archive'
                ? 'archived'
                : 'unarchived';
        toast.success(
          // An unarchived campaign comes back paused rather than sending —
          // say so, so nobody has to discover it from the status badge.
          action === 'unarchive' && data.status === 'paused'
            ? 'Campaign unarchived — it is paused, resume it when you want it sending again.'
            : `Campaign ${past}.`,
        );
        if (data.warning) toast.warning(data.warning);
      } else {
        setError(data.error || `${action} failed`);
        toast.error(data.error || `${action} failed`);
      }
    } catch {
      setError(`${action} request failed`);
      toast.error(`${action} request failed`);
    }
    setActionLoading('');
  };

  /**
   * Soft-deletes the campaign and leaves the page — the detail route 404s on a
   * deleted campaign, so staying here would just render "not found".
   * Recoverable from Settings › Deleted Campaigns for CAMPAIGN_RETENTION_DAYS
   * (30), which the dialog states before anything happens.
   */
  const deleteCampaign = async () => {
    const label = campaign?.name || 'this untitled draft';
    const canRecall = campaign?.campaignType === 'linkedin' || campaign?.campaignType === 'multi';
    const message = `Delete “${label}”? It stops sending and disappears from your dashboard. You can restore it for 30 days under Settings › Deleted Campaigns — after that it is deleted permanently.`;
    let recall = false;
    if (canRecall) {
      const res = await confirmDialogWithCheckbox(message, {
        title: 'Delete Campaign',
        confirmLabel: 'Delete campaign',
        confirmClass: 'btn-error',
        checkboxLabel:
          'Also recall this campaign’s connection requests — cancel the pending ones and withdraw its already-sent invitations on LinkedIn. This can’t be undone by Restore.',
      });
      if (!res.confirmed) return;
      recall = res.checked;
    } else if (
      !(await confirmDialog(message, {
        title: 'Delete Campaign',
        confirmLabel: 'Delete campaign',
        confirmClass: 'btn-error',
      }))
    ) {
      return;
    }
    setActionLoading('delete');
    setError('');
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}${recall ? '?recallInvitations=true' : ''}`,
        { method: 'DELETE' },
      );
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        setError(data?.error || 'Delete failed');
        toast.error(data?.error || 'Delete failed');
        setActionLoading('');
        return;
      }
      toast.success(`Campaign deleted — restorable for ${data.retentionDays ?? 30} days in Settings.`);
      if (recall && data.recalled) {
        const r = data.recalled;
        toast.info(
          `Connection requests recalled: ${r.sentRecalled} withdrawn, ${r.jobsCancelled} pending cancelled.`,
        );
      }
      if (data.warning) toast.warning(data.warning);
      sdrRouter.goTo('dashboard');
    } catch {
      setError('Delete request failed');
      toast.error('Delete request failed');
      setActionLoading('');
    }
  };

  const duplicateCampaign = async () => {
    setDuplicating(true);
    setError('');
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/duplicate`, { method: 'POST' });
      const data = await resp.json();
      if (data.ok && data.campaign?._id) {
        toast.success('Campaign duplicated.');
        sdrRouter.goTo('editCampaign', { params: { campaignId: data.campaign._id } } as any);
      } else {
        setError(data.error || 'Duplication failed');
        toast.error(data.error || 'Duplication failed');
      }
    } catch {
      setError('Duplication request failed');
      toast.error('Duplication request failed');
    }
    setDuplicating(false);
  };

  // ── Pre-launch message editing ─────────────────────────────────────────
  const startEditMessage = (m: CampaignMessage) => {
    setEditingMessageId(m._id);
    setEditSubjectDraft(m.subject ?? '');
    setEditBodyDraft(m.body ?? '');
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditSubjectDraft('');
    setEditBodyDraft('');
  };

  const saveEditMessage = async (messageId: string) => {
    setSavingMessageId(messageId);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/messages/${encodeURIComponent(messageId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: editSubjectDraft, body: editBodyDraft }),
        },
      );
      const data = await resp.json();
      if (data.ok) {
        await fetchMessages();
        cancelEditMessage();
      } else {
        setError(data.error || 'Failed to save message');
      }
    } catch {
      setError('Failed to save message');
    }
    setSavingMessageId(null);
  };

  const regenerateMessage = async (messageId: string) => {
    setRegeneratingMessageId(messageId);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/messages/${encodeURIComponent(messageId)}/regenerate`,
        { method: 'POST' },
      );
      const data = await resp.json();
      if (data.ok) {
        await fetchMessages();
      } else {
        setError(data.error || 'Regeneration failed');
      }
    } catch {
      setError('Regeneration request failed');
    }
    setRegeneratingMessageId(null);
  };

  const deleteLead = async (lead: LeadData) => {
    const confirmed = await confirmDialog(
      `Delete ${lead.firstName} ${lead.lastName} and all generated messages for this lead?`,
      { title: 'Delete Lead', confirmLabel: 'Delete', confirmClass: 'btn-error' },
    );
    if (!confirmed) return;
    setDeletingLeadId(lead._id);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/leads/${encodeURIComponent(lead._id)}`,
        { method: 'DELETE' },
      );
      const data = await resp.json();
      if (data.ok) {
        if (expandedLeadId === lead._id) setExpandedLeadId(null);
        await fetchData();
      } else {
        setError(data.error || 'Delete failed');
      }
    } catch {
      setError('Delete request failed');
    }
    setDeletingLeadId(null);
  };

  const launchCampaign = async () => {
    const totalLeadsCount = leads.length;
    const totalMessagesCount = messages.filter(m => m.generationStatus === 'ready').length;

    const confirmed = await confirmDialog(
      `Start the 1-hour launch cool-down? ${totalLeadsCount} lead${totalLeadsCount === 1 ? '' : 's'} with ${totalMessagesCount} message${totalMessagesCount === 1 ? '' : 's'} will be queued. Nothing sends yet — you can still edit messages, leads, steps, or the Sales Rep during the cool-down (each edit restarts the hour), or skip it to launch immediately.`,
      { title: 'Launch Campaign', confirmLabel: 'Start Cool-Down', confirmClass: 'btn-success' },
    );
    if (!confirmed) return;

    if (isStaging()) {
      const stagingConfirmed = await confirmDialog(
        `YOU ARE ON STAGING. Once the cool-down elapses (or you skip it), REAL MESSAGES (Emails, LinkedIn connection requests/DMs, ...) will be sent to REAL PEOPLE.`,
        { title: 'STAGING ENVIRONMENT | MAILGATE Check', confirmLabel: 'Proceed', confirmClass: 'btn-success' },
      );
      if (!stagingConfirmed) return;
    }

    setLaunching(true);
    setError('');
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/launch`, { method: 'POST' });
      const data = await resp.json();
      if (data.ok) {
        await fetchData();
        toast.success('1-hour launch cool-down started.');
      } else {
        setError(data.error || 'Launch failed');
        toast.error(data.error || 'Launch failed');
      }
    } catch {
      setError('Launch request failed');
      toast.error('Launch request failed');
    }
    setLaunching(false);
  };

  const skipCooldown = async () => {
    const confirmed = await confirmDialog('Skip the remaining cool-down and launch this campaign right now?', {
      title: 'Skip Cool-Down',
      confirmLabel: 'Launch Now',
      confirmClass: 'btn-success',
    });
    if (!confirmed) return;
    setLaunching(true);
    setError('');
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/launch/skip`, { method: 'POST' });
      const data = await resp.json();
      if (!data.ok) {
        setError(data.error || 'Launch failed');
        toast.error(data.error || 'Launch failed');
        setLaunching(false);
        return;
      }
      if (data.resuming) {
        // Resuming runs server-side: the campaign moves through 'generating'
        // (any step added during the pause is written now) and lands on
        // 'active'. This request no longer waits on that generation — which is
        // what used to time out — so poll until the campaign leaves the
        // cool-down, keeping the button disabled meanwhile; the status-based
        // poll above then carries 'generating' the rest of the way to 'active'.
        toast.success('Launching — any new messages are being generated…');
        for (let i = 0; i < 60; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          let status: string | undefined;
          try {
            const c = await (await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}`)).json();
            status = c?.campaign?.status;
          } catch {
            /* transient — keep waiting */
          }
          if (status && status !== 'preparing_to_launch') break;
        }
        await fetchData();
      } else {
        await fetchData();
        toast.success('Campaign launched.');
      }
    } catch {
      setError('Launch request failed');
      toast.error('Launch request failed');
    }
    setLaunching(false);
  };

  const isPreLaunchEditable = (status: string | undefined) =>
    status === 'generating' || status === 'ready_to_launch' || status === 'preparing_to_launch';

  /**
   * A lead is "done" once status === 'completed' OR we already sent a reply
   * draft for it. The DB-level status is the authoritative signal but it's
   * only set after the next sync tick — meanwhile, a sent draft is proof
   * that the campaign is over for this lead, so we surface "done" immediately.
   */
  const leadIdsWithSentDraft = new Set(drafts.filter(d => d.status === 'sent').map(d => d.leadId));
  const isLeadCompleted = (lead: LeadData) => lead.status === 'completed' || leadIdsWithSentDraft.has(lead._id);

  /**
   * A step is "capable" for a lead when the lead has the channel-specific
   * data the step needs. LinkedIn steps require a `linkedinUrl`, email steps
   * require an `email` (always true here — email is the lead primary key).
   * Ineligible steps are skipped during message generation on the backend
   * (see `campaign-prepare.ts`), so the timeline must visually skip them too
   * instead of misleadingly showing them as "Next".
   */
  const stepRequiresLinkedin = (channel: string) => channel === 'linkedin_connect' || channel === 'linkedin_dm';
  const stepRequiresEmail = (channel: string) => channel === 'email' || channel === 'email_followup';
  const canLeadDoStep = (lead: LeadData, channel: string): boolean => {
    // `.trim()` matches the server's own eligibility test (campaign-prepare.ts
    // and evaluate-lead-done.ts) — a whitespace-only field must count as
    // missing on both sides, or the UI promises a step the backend skips.
    if (stepRequiresLinkedin(channel)) return !!(lead.linkedinUrl ?? '').trim();
    if (stepRequiresEmail(channel)) return !!(lead.email ?? '').trim();
    return true;
  };
  /**
   * Per-lead "done / total" step progress for the leads table. Only steps the
   * lead is eligible for count toward the total — a LinkedIn-only lead on a
   * campaign with one email step has 2 steps, not "0/3". `currentStep`
   * indexes the FULL sequence, so "done" counts the eligible steps the lead
   * has already moved past.
   */
  const leadStepProgress = (lead: LeadData): string => {
    const seq = campaign?.sequence ?? [];
    if (seq.length === 0) return `${lead.currentStep} / ?`;
    const total = seq.filter(s => canLeadDoStep(lead, s.channel)).length;
    const done = seq.filter((s, i) => i < lead.currentStep && canLeadDoStep(lead, s.channel)).length;
    return `${done} / ${total}`;
  };
  /**
   * A `linkedin_connect` step is moot once the lead is already a connection
   * (`acceptedAt` set). There is nothing to send, so the step is struck through
   * from the start and "Next" skips straight to the following step (e.g. the DM)
   * instead of misleadingly offering to connect again.
   */
  const isStepAlreadySatisfied = (lead: LeadData, channel: string): boolean =>
    // Already-connected counts too: there is nothing left to send for a connect
    // step either way. Before `alreadyConnected` existed both cases shared
    // `acceptedAt`, so this check kept working by accident — spelled out now.
    channel === 'linkedin_connect' && (!!lead.acceptedAt || !!lead.alreadyConnected);

  /** Compute next step info for a lead */
  const CHANNEL_LABELS: Record<string, string> = {
    email: 'Email',
    email_followup: 'Follow-up Email',
    linkedin_connect: 'LinkedIn Connect',
    linkedin_dm: 'LinkedIn DM',
  };

  /**
   * The lead's next step, as the SERVER sees it.
   *
   * This used to walk the sequence from `currentStep`, which is a different
   * question: for a lead whose connection request was skipped, `currentStep`
   * has already moved past it, so the column announced "LinkedIn DM" while the
   * step actually waiting to run was the connection request. Third place in
   * this page where the same answer was being recomputed, third disagreement.
   */
  const getNextStepInfo = (lead: LeadData): { name: string; date: string | null } | null => {
    const next = lead.nextStep;
    if (!next?.channel) return null;
    const name = CHANNEL_LABELS[next.channel] ?? next.channel;
    const date = next.scheduledAt ? new Date(next.scheduledAt).toLocaleDateString() : null;
    return { name, date };
  };

  /**
   * The Next Step cell when there is no next step. "No channel" is four
   * different situations, and only two of them have earned a green Completed —
   * a lead added after launch whose jobs are not created yet was announcing
   * itself as done before it had ever been contacted.
   */
  const noNextStepCell = (lead: LeadData) => {
    const because = lead.nextStep?.noneBecause;
    if (because === 'no_steps') {
      return html`<span class="text-base-content/50 text-xs" title=${lead.nextStep?.reason ?? ''}>Not queued yet</span>`;
    }
    if (because === 'replied') {
      return html`<span class="text-base-content/40 text-xs" title=${lead.nextStep?.reason ?? ''}>—</span>`;
    }
    return html`<span class="text-success text-xs">Completed</span>`;
  };

  if (loading) {
    return html`
      <div class="mx-auto flex max-w-6xl flex-col gap-6 p-6">
        <div class="flex flex-col gap-3">
          ${skeletonLine({ width: 'w-32', height: 'h-3' })} ${skeletonLine({ width: 'w-64', height: 'h-8' })}
          ${skeletonLine({ width: 'w-96', height: 'h-4' })}
        </div>
        ${skeletonRows(4)}
      </div>
    `;
  }

  if (!campaign) {
    return html`<div class="mx-auto max-w-5xl p-6">
      <div class="alert alert-error" role="alert">
        We couldn’t find that campaign. It may have been deleted.
        <a class="link ml-2" @click=${() => sdrRouter.goTo('dashboard')}>Back to Dashboard</a>
      </div>
    </div>`;
  }

  const statusBadge = (status: string) => {
    const classes: Record<string, string> = {
      active: 'badge-success',
      paused: 'badge-warning',
      draft: 'badge-ghost',
      generating: 'badge-info',
      ready_to_launch: 'badge-warning',
      preparing_to_launch: 'badge-warning',
      completed: 'badge-info',
      archived: 'badge-neutral',
      error: 'badge-error',
    };
    const labels: Record<string, string> = { ready_to_launch: 'ready to launch', preparing_to_launch: 'cool-down' };
    return html`<span class="badge badge-lg ${classes[status] ?? 'badge-ghost'}">${labels[status] ?? status}</span>`;
  };

  const statCard = (
    label: string,
    value: number | string,
    icon: string,
    colorClass: string,
    /** Optional breakdown lines shown on hover — e.g. real sends vs. already-connected vs. still-pending. */
    tooltipLines?: string[],
    /**
     * When set, the card becomes a filter for the leads table below: clicking
     * narrows the table to that bucket, clicking the active card clears it. The
     * headline number and the way to see WHICH leads it counts are then the
     * same control, which is the whole point — a "3 Replied" card you can't
     * click leaves you scrolling 100 rows to find those 3.
     */
    filterKey?: LeadFilterKey,
  ) => {
    const active = filterKey !== undefined && leadFilter === filterKey;
    const matches = filterKey !== undefined ? leadCountFor(filterKey) : 0;
    // Always real functions rather than a conditional `nothing`: lit documents
    // `nothing` for removing ATTRIBUTES, not for event bindings, so a no-op
    // closure is the safe way to express "this card isn't clickable".
    const applyFilter = () => {
      if (filterKey === undefined) return;
      setLeadFilter(active ? 'all' : filterKey);
      // Jump to the table so the effect of the click is visible even when the
      // lead list sits far below the fold.
      requestAnimationFrame(() =>
        document.getElementById('leads-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    };
    const onKeydown = (e: KeyboardEvent) => {
      if (filterKey === undefined) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      applyFilter();
    };
    return html`
    <div
      class="card bg-base-100 min-w-[100px] flex-1 shadow-sm ${tooltipLines ? 'group relative' : ''} ${
        filterKey !== undefined ? 'cursor-pointer transition hover:shadow-md' : ''
      } ${active ? 'ring-primary ring-2' : ''}"
      role=${filterKey !== undefined ? 'button' : nothing}
      tabindex=${filterKey !== undefined ? '0' : nothing}
      title=${
        filterKey === undefined
          ? nothing
          : active
            ? 'Click to clear the filter'
            : `Show only these ${matches} lead${matches === 1 ? '' : 's'}`
      }
      @click=${applyFilter}
      @keydown=${onKeydown}
    >
      <div class="card-body items-center py-4 text-center">
        <span class="icon-[material-symbols--${icon}] ${colorClass} text-2xl"></span>
        <p class="text-2xl font-bold">${value}</p>
        <p class="text-base-content/60 text-xs">${label}</p>
      </div>
      ${
        tooltipLines
          ? html`
            <div
              class="border-base-300 bg-base-100 pointer-events-none absolute top-full left-1/2 z-20 mt-1 hidden w-max -translate-x-1/2 flex-col gap-0.5 rounded-lg border p-2 text-left text-xs whitespace-nowrap shadow-lg group-hover:flex"
            >
              ${tooltipLines.map(line => html`<span>${line}</span>`)}
            </div>
          `
          : nothing
      }
    </div>
  `;
  };

  const totalSent = campaign.totalSent ?? 0;
  const totalReplied = campaign.totalReplied ?? 0;
  const totalDone = campaign.totalDone ?? 0;
  // The loaded leads, not `leadsProcessed`. That field is written at launch and
  // never again, so a campaign topped up from 20 to 100 leads kept reporting 20
  // — the card said 20 while the table beneath it said 100. A count that
  // disagrees with the list it summarises is worse than no count.
  const totalLeads = leads.length;
  // NOT `campaign.totalBounced ?? 0`. On the armed path nothing polls for
  // bounces, so a zero there is an absence of measurement wearing the clothes
  // of a measurement — the rule lives in one place, `measured-counters.ts`.
  //
  // Feed it the bounces recorded on the leads we already hold: the Unipile
  // webhook writes `lead.bouncedAt` the moment a delivery-status notice arrives,
  // but `campaign.totalBounced` only catches up on a sync. Without this the card
  // read "—" while the Bounced filter below already listed the dead address —
  // the two must agree, and the freshest fact wins.
  const recordedBounces = leads.filter(l => !!l.bouncedAt || l.status === 'bounced').length;
  const bounce = bounceReading(campaign, recordedBounces);
  // Whether "no automatic DM" is a thing this campaign can even do. A sequence
  // with no DM step withheld nothing, so calling its already-connected leads
  // "skipped" would invent a decision nobody made.
  const campaignHasDmStep = (campaign.sequence ?? []).some(step => step.channel === 'linkedin_dm');
  const doneRate = totalLeads > 0 ? Math.round((totalDone / totalLeads) * 100) : 0;
  const pendingCount = leads.filter(l => l.status === 'pending').length;
  const liEligibleLeadsCount = leads.filter(l => !!l.linkedinUrl).length;

  // ── Leads table filtering & sorting ──────────────────────────────────────
  /**
   * Per-lead predicate for each bucket. Deliberately driven by the lead's own
   * timestamps rather than `lead.status`, because `status` has no 'accepted'
   * value at all — an accepted connection request otherwise stays invisible in
   * the list, which is exactly the "I hunt for 20 minutes" problem.
   */
  // The chip-relevant buckets reuse the pure predicates from lead-state-chip.ts,
  // so a chip and its bucket can never disagree (the invariant this list was
  // built on). Only the closure-dependent buckets — `skippedAlreadyConnected`
  // (needs the campaign's DM step), `done`/`repliesSent` (need the drafts), and
  // the cross-cutting `sent`/`opened`/`waiting`/`all` — stay inline here.
  const leadFilterPredicates: Record<LeadFilterKey, (l: LeadData) => boolean> = {
    all: () => true,
    pending: chipPredicates.pending,
    // Visible in the Status column, so it has to be filterable. Every status a
    // lead can display now has a bucket — a chip you can read but not act on
    // is half a feature.
    contacted: chipPredicates.contacted,
    // A real invite went out — excludes already-connected leads, which also
    // carry connectionSentAt even though nothing was ever sent to them.
    connSent: chipPredicates.connSent,
    accepted: chipPredicates.accepted,
    alreadyConnected: chipPredicates.alreadyConnected,
    // The requirement from Sales Factory, as a bucket. A lead who was already a
    // connection gets no automatic DM (helpers/dm-eligibility.ts), so on a
    // campaign that HAS a DM step this is a distinct outcome and not merely a
    // property of the relationship — it says a step was withheld and is waiting
    // on a person. Once somebody sends it by hand, `dmSentAt` moves the lead on
    // to "DM sent" and this bucket lets go of it.
    // Two ways in: this campaign's own LinkedIn observation withheld the DM
    // (`dmWithheld`), OR the person is on the global pre-existing-connection
    // list (`knownConnection`) — the latter is what surfaces the mark on a lead
    // that never had a LinkedIn step here (a pure email campaign), whose
    // automatic sends the server withholds all the same.
    skippedAlreadyConnected: l =>
      dmWithheld({ alreadyConnected: l.alreadyConnected, dmSentAt: l.dmSentAt, campaignHasDmStep }) ||
      !!l.knownConnection,
    invitePending: chipPredicates.invitePending,
    // They invited US and it has not been accepted. Had no chip and no filter,
    // so a lead in this state was indistinguishable from one nobody had touched.
    inviteReceived: chipPredicates.inviteReceived,
    // We invited them and they dismissed it. Terminal for the connection step:
    // LinkedIn refuses another invitation to the same person for weeks, so the
    // scheduler will not re-invite. Without a chip this read as plain 'pending'
    // — a lead that looked untouched while it was in fact a closed door, which
    // is exactly what made one ignored recipient hold a whole account.
    inviteSentIgnored: chipPredicates.inviteSentIgnored,
    // Nothing outstanding in either direction: no connection, no invitation.
    // These are the leads that will never move on their own — the exact state
    // eight leads sat in, invisibly, for three days.
    noInvite: chipPredicates.noInvite,
    // The provider said something we could not read. Worth surfacing rather
    // than quietly lumping in with "not connected".
    connectionUnknown: chipPredicates.connectionUnknown,
    // A step parked for a legitimate reason — outside business hours, a daily
    // limit, or LinkedIn blocking a re-invite. Waiting is not failing, but it
    // is also not progress, and you need to be able to list it.
    waiting: l => !!l.nextStep?.waitingReason,
    dmSent: chipPredicates.dmSent,
    sent: l => !!l.sentAt,
    opened: l => !!l.openedAt,
    replied: chipPredicates.replied,
    repliesSent: l => leadIdsWithSentDraft.has(l._id),
    bounced: chipPredicates.bounced,
    // Split out of 'bounced'. An unreachable address and a person who asked to
    // be left alone are different facts with different consequences, and the
    // combined bucket made the second impossible to list. Includes a lead
    // suppressed globally, whose row status is 'opted_out' — see chipPredicates.
    optedOut: chipPredicates.optedOut,
    done: l => isLeadCompleted(l) || !!l.doneAt,
  };
  const leadFilterLabels: Record<LeadFilterKey, string> = {
    all: 'All leads',
    pending: 'Pending',
    contacted: 'Contacted',
    connSent: 'Connection sent',
    accepted: 'Accepted',
    alreadyConnected: 'Already connected',
    skippedAlreadyConnected: 'Skipped — already connected',
    invitePending: 'Invite already pending',
    inviteReceived: 'They invited us',
    inviteSentIgnored: 'Skipped — invitation dismissed',
    noInvite: 'No invitation — nothing outstanding',
    connectionUnknown: 'Connection state unreadable',
    waiting: 'Waiting on something',
    dmSent: 'DM sent',
    sent: 'Sent',
    opened: 'Opened',
    replied: 'Replied',
    repliesSent: 'Replies sent',
    bounced: 'Bounced',
    optedOut: 'Opted out',
    done: 'Done',
  };
  /** How many leads a bucket would show — used for the click hint on each stat card. */
  const leadCountFor = (key: LeadFilterKey) => leads.filter(leadFilterPredicates[key]).length;

  /**
   * Where each bucket sits in the filter menu — roughly the order a lead moves
   * through them.
   *
   * A `Record` of the full key union, NOT an array: an array typed
   * `LeadFilterKey[]` accepts a list that is missing entries, and a missing
   * entry means a filter nobody can reach. That is precisely what the page
   * shipped with — several buckets existed with no way to select them, because
   * stat cards were the only entry point and not every bucket has a card.
   * Adding a filter without giving it a position is now a compile error.
   */
  const leadFilterRank: Record<LeadFilterKey, number> = {
    all: 0,
    pending: 1,
    contacted: 2,
    connSent: 3,
    invitePending: 4,
    inviteReceived: 5,
    noInvite: 6,
    accepted: 7,
    alreadyConnected: 8,
    skippedAlreadyConnected: 9,
    inviteSentIgnored: 10,
    dmSent: 11,
    sent: 12,
    opened: 13,
    replied: 14,
    repliesSent: 15,
    waiting: 16,
    connectionUnknown: 17,
    bounced: 18,
    optedOut: 19,
    done: 20,
  };
  const leadFilterOrder = (Object.keys(leadFilterRank) as LeadFilterKey[]).sort(
    (a, b) => leadFilterRank[a] - leadFilterRank[b],
  );

  // The Status column shows ONE chip: the most operative state a lead has
  // reached. The ordered list, the pure predicates, and the precedence (opt-out
  // ahead of replied) live in `lead-state-chip.ts`, where they can be tested
  // directly; the buckets above reuse the same predicates so a chip and its
  // filter can never disagree.
  //
  // Five filters deliberately have no chip, and that is a decision rather than
  // an omission: `all` is not a state; `sent` / `opened` / `repliesSent` are
  // cross-cutting facts a lead can hold in any state, so they would compete
  // with the one that describes where it stands; and `waiting` moved to the
  // Next Step column, where the question it answers actually arises.

  /**
   * The full history behind the chip, as hover text. Nothing is lost by showing
   * one badge — the detail moves here, where it is available on demand instead
   * of competing for the same twelve pixels on every row.
   */
  const leadStateTooltip = (l: LeadData): string => {
    const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : null);
    const lines = [
      l.repliedAt ? `Replied ${when(l.repliedAt)}` : null,
      l.dmSentAt ? `DM sent ${when(l.dmSentAt)}` : null,
      l.acceptedAt ? `Accepted our invite ${when(l.acceptedAt)}` : null,
      dmWithheld({ alreadyConnected: l.alreadyConnected, dmSentAt: l.dmSentAt, campaignHasDmStep })
        ? 'Was already a connection before this campaign — the sequence DM was NOT sent automatically'
        : l.alreadyConnected
          ? 'Was already a connection before this campaign'
          : null,
      l.invitePendingElsewhere ? 'An invitation was already outstanding — none of ours was sent' : null,
      l.connectionSentAt && !l.alreadyConnected && !l.invitePendingElsewhere
        ? `Our invitation sent ${when(l.connectionSentAt)}`
        : null,
      l.connectionState === 'invite_sent_ignored'
        ? 'This lead was invited before and dismissed the request. LinkedIn refuses another invitation to ' +
          'the same person for weeks and no longer shows the dismissed one on their profile, so no further ' +
          'invitation is sent.'
        : null,
      l.connectionState === 'not_connected_no_invite'
        ? 'No connection and nothing outstanding — cannot progress until an invitation goes out'
        : null,
      l.connectionState === 'unknown' ? 'LinkedIn returned a value this app does not recognise' : null,
      // The reason a step is parked belongs to the step, not to the lead — but
      // it is worth having here too, since this is where the eye lands first.
      l.nextStep?.waitingReason ? `Waiting: ${l.nextStep.waitingReason}` : null,
    ].filter(Boolean);
    return lines.length > 0 ? lines.join('\n') : 'Nothing has happened for this lead yet';
  };

  const leadStateChip = (l: LeadData) => {
    const state = resolveLeadStateChip(l, {
      isCompleted: isLeadCompleted(l),
      // The one bucket that needs the campaign's DM step — computed here where
      // that is known, and shared with the filter so the two agree.
      isSkippedAlreadyConnected: leadFilterPredicates.skippedAlreadyConnected(l),
    });
    // A lead carrying some status this list does not model still gets shown —
    // as itself. Rendering nothing would quietly turn "we don't recognise this"
    // into "nothing to report", which is the exact substitution that started
    // this whole investigation.
    if (!state) {
      return html`<span class="badge badge-ghost badge-sm shrink-0 whitespace-nowrap" title=${leadStateTooltip(l)}
        >${l.status || 'unknown'}</span
      >`;
    }
    return html`<span
      class="badge badge-sm shrink-0 gap-1 whitespace-nowrap ${state.cls}"
      title=${leadStateTooltip(l)}
      ><span class="icon-[material-symbols--${state.icon}] text-xs"></span>${state.label}</span
    >`;
  };

  // Export every lead as CSV for a CRM. Built entirely from the already-loaded
  // `leads` state, and — crucially — the Status column reuses the SAME
  // resolveLeadStateChip the table renders, and the Sending Account the same
  // resolveLeadSendingAccount, so the file matches what the operator sees on
  // screen rather than a re-derivation that could drift from it.
  const exportLeadsCsv = () => {
    const csv = buildCampaignLeadsCsv(leads, {
      statusLabel: l =>
        resolveLeadStateChip(l, {
          isCompleted: isLeadCompleted(l),
          isSkippedAlreadyConnected: leadFilterPredicates.skippedAlreadyConnected(l),
        })?.label ??
        l.status ??
        '',
      sendingAccount: l => resolveLeadSendingAccount(linkedinJobs, l._id, accountNames)?.name ?? '',
      salesRep: l => (l.aiEmployeeId ? (salesRepsById[l.aiEmployeeId] ?? '') : ''),
    });
    downloadTextFile(`${campaignFilenameSlug(campaign?.name)}_leads.csv`, csv, 'text/csv;charset=utf-8;');
  };

  // The Status cell doubles as the Manual Overwrite control: the coloured chip
  // stays visible, and a transparent native <select> laid over it opens on a
  // click — click the chip, pick a status, done. Native on purpose: its option
  // list renders in the browser's top layer, so it is never clipped by the
  // table's own horizontal scroll box the way an absolutely-positioned dropdown
  // would be. stopPropagation stops the click also toggling the row's accordion.
  const leadStatusChipMenu = (l: LeadData) => {
    const saving = savingStatusLeadId === l._id;
    const current = l.manualStatus ?? '';
    return html`
      <div class="relative inline-flex items-center gap-0.5" @click=${(e: Event) => e.stopPropagation()}>
        ${leadStateChip(l)}
        ${
          saving
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--arrow-drop-down] text-base-content/40 text-base"></span>`
        }
        <select
          class="absolute inset-0 cursor-pointer opacity-0"
          aria-label="Set lead status manually"
          ?disabled=${saving}
          .value=${current}
          @change=${(e: Event) => void setLeadStatus(l, (e.target as HTMLSelectElement).value)}
        >
          ${MANUAL_STATUS_OPTIONS.map(
            o => html`<option value=${o.value} ?selected=${current === o.value}>${o.label}</option>`,
          )}
        </select>
      </div>
    `;
  };

  /** Most recent moment anything happened for this lead — the basis for "last touched" sorting. */
  const lastTouchedAt = (l: LeadData): number => {
    const stamps = [
      l.lastContactedAt,
      l.sentAt,
      l.openedAt,
      l.repliedAt,
      l.connectionSentAt,
      l.acceptedAt,
      l.dmSentAt,
      l.bouncedAt,
      l.doneAt,
    ]
      .filter((s): s is string => !!s)
      .map(s => new Date(s).getTime())
      .filter(n => Number.isFinite(n));
    return stamps.length > 0 ? Math.max(...stamps) : 0;
  };
  /** Higher = more interesting. Puts the leads that need a human first. */
  const engagementRank = (l: LeadData): number => {
    if (l.repliedAt || l.status === 'replied') return 5;
    if (l.acceptedAt) return 4;
    if (l.dmSentAt) return 3;
    if (l.openedAt) return 2;
    if (l.connectionSentAt || l.sentAt) return 1;
    return 0;
  };
  const visibleLeads = (() => {
    const filtered = filterLeadsBySearch(leads.filter(leadFilterPredicates[leadFilter]), leadSearch);
    const sorted = [...filtered];
    switch (leadSort) {
      case 'touchedOldest':
        // Leads never touched (0) sort last here rather than first — an
        // untouched lead isn't "the oldest interaction", it has none.
        sorted.sort((a, b) => {
          const ta = lastTouchedAt(a);
          const tb = lastTouchedAt(b);
          if (ta === 0 && tb === 0) return 0;
          if (ta === 0) return 1;
          if (tb === 0) return -1;
          return ta - tb;
        });
        break;
      case 'touchedNewest':
        sorted.sort((a, b) => lastTouchedAt(b) - lastTouchedAt(a));
        break;
      case 'nameAsc':
        sorted.sort((a, b) =>
          `${a.firstName} ${a.lastName}`.trim().localeCompare(`${b.firstName} ${b.lastName}`.trim()),
        );
        break;
      default:
        // Relevance: replies first, then acceptances, … and within the same
        // rank the most recent activity on top, so the freshest reply leads.
        sorted.sort((a, b) => engagementRank(b) - engagementRank(a) || lastTouchedAt(b) - lastTouchedAt(a));
    }
    return sorted;
  })();
  const connSentTooltip = (() => {
    // Counted from the projected leads, never the stored campaign counters:
    // those are a stale snapshot (the tick worker no longer maintains them),
    // so the header read 0 while 24 invites had gone out. `leadCountFor` runs
    // the same predicate the filter chip does, so the number and the list it
    // opens cannot disagree.
    const sent = leadCountFor('connSent');
    const alreadyConnected = leadCountFor('alreadyConnected');
    const invitePending = leadCountFor('invitePending');
    const total = liEligibleLeadsCount;
    const stillPending = Math.max(0, total - sent - alreadyConnected - invitePending);
    const lines = [`${sent}/${total} connection requests sent`, `${alreadyConnected}/${total} already connected`];
    // Called out separately because it is the one bucket that looks like
    // progress but is not: nothing was sent by us AND there is no connection,
    // so these leads cannot receive a DM until the person accepts.
    if (invitePending > 0) lines.push(`${invitePending}/${total} had an invite already pending (not connected)`);
    if (stillPending > 0) lines.push(`${stillPending}/${total} still pending`);
    return lines;
  })();

  const sequence = campaign.sequence ?? [];

  /** Render the step timeline for a lead */
  const renderStepTimeline = (lead: LeadData) => {
    if (sequence.length === 0) return html`<p class="text-base-content/50 text-sm">No sequence configured</p>`;
    const channelIcons: Record<string, string> = {
      email: 'mail',
      email_followup: 'forward-to-inbox',
      linkedin_connect: 'person-add',
      linkedin_dm: 'chat',
    };
    const channelLabels: Record<string, string> = {
      email: 'Email',
      email_followup: 'Follow-up Email',
      linkedin_connect: 'LinkedIn Connect',
      linkedin_dm: 'LinkedIn DM',
    };
    // Once the lead has replied, every "if no reply" step is moot — show it
    // struck-through. Once we've actually sent a reply (status === 'completed'
    // or doneAt set), the entire remaining sequence is over.
    const hasReplied = !!lead.repliedAt || isLeadCompleted(lead) || lead.status === 'replied';
    const sequenceFinished = !!lead.doneAt || isLeadCompleted(lead);
    // "Next" must point at the first step the lead can actually do — channels
    // the lead is missing data for (no LinkedIn URL, no email) are skipped.
    let nextEligibleIdx = lead.currentStep;
    while (nextEligibleIdx < sequence.length) {
      const s = sequence[nextEligibleIdx];
      if (!s) break;
      if (canLeadDoStep(lead, s.channel) && !isStepAlreadySatisfied(lead, s.channel)) break;
      nextEligibleIdx++;
    }
    // Steps the lead can actually do — a channel the lead lacks contact data
    // for is simply not part of this lead's sequence (a LinkedIn-only lead on
    // a campaign with an email step sees a 2-step timeline, not a 3-step one
    // with a struck-through hole). Original indices are kept for the
    // currentStep / nextEligibleIdx math; numbering uses the visible position.
    const visibleSteps = sequence
      .map((step, i) => ({ step, i }))
      .filter(({ step }) => canLeadDoStep(lead, step.channel));
    return html`
      <div class="flex flex-col gap-2">
        ${
          visibleSteps.length === 0
            ? html`<span class="text-warning text-xs">No eligible steps for this lead (missing contact data)</span>`
            : nothing
        }
        ${visibleSteps.map(({ step, i }, pos) => {
          const isDone = i < lead.currentStep;
          // Already a connection → the connect step is moot; show it struck through.
          const alreadyConnected = !isDone && isStepAlreadySatisfied(lead, step.channel);
          const isFutureNoReply = !isDone && hasReplied && step.condition === 'no_reply';
          const isSkipped = alreadyConnected || (!isDone && sequenceFinished) || isFutureNoReply;
          const isCurrent = !sequenceFinished && !isFutureNoReply && !alreadyConnected && i === nextEligibleIdx;
          const icon = channelIcons[step.channel] ?? 'description';
          const label = channelLabels[step.channel] ?? step.channel;
          return html`
            <div class="flex items-center gap-3">
              <div class="flex flex-col items-center">
                <div
                  class="${
                    isDone
                      ? 'bg-success text-success-content'
                      : isSkipped
                        ? 'bg-base-300 text-base-content/30'
                        : isCurrent
                          ? 'bg-primary text-primary-content'
                          : 'bg-base-300 text-base-content/50'
                  } flex h-8 w-8 items-center justify-center rounded-full text-sm"
                >
                  ${
                    isDone
                      ? html`<span class="icon-[material-symbols--check] text-base"></span>`
                      : isSkipped
                        ? html`<span class="icon-[material-symbols--block] text-base"></span>`
                        : html`<span>${pos + 1}</span>`
                  }
                </div>
                ${
                  pos < visibleSteps.length - 1
                    ? html`<div class="${isDone ? 'bg-success' : 'bg-base-300'} h-4 w-0.5"></div>`
                    : nothing
                }
              </div>
              <div class="flex flex-1 items-center gap-2">
                <span
                  class="icon-[material-symbols--${icon}] ${
                    isDone
                      ? 'text-success'
                      : isSkipped
                        ? 'text-base-content/30'
                        : isCurrent
                          ? 'text-primary'
                          : 'text-base-content/40'
                  } text-base"
                ></span>
                <span
                  class="${
                    isDone
                      ? 'text-success'
                      : isSkipped
                        ? 'text-base-content/30 line-through'
                        : isCurrent
                          ? 'font-medium text-primary'
                          : 'text-base-content/50'
                  } text-sm"
                  >${label}</span
                >
                ${isCurrent ? html`<span class="badge badge-primary badge-xs">Next</span>` : nothing}
                ${isDone ? html`<span class="text-success/60 text-xs">✓</span>` : nothing}
                ${
                  isSkipped
                    ? html`<span class="text-base-content/30 text-xs"
                      >${alreadyConnected ? 'Already connected' : 'Skipped'}</span
                    >`
                    : nothing
                }
              </div>
              ${
                step.delayValue > 0
                  ? html`<span class="text-base-content/40 text-xs">${step.delayValue} ${step.delayUnit}</span>`
                  : nothing
              }
            </div>
          `;
        })}
        ${
          // Completed = past every step THIS lead is eligible for, not every
          // step in the campaign — a LinkedIn-only lead on a [connect, dm,
          // email] sequence is finished at currentStep=2, and comparing
          // against the full sequence length left it stuck showing "in
          // progress" next to a "2 / 2" step count. Empty visibleSteps is
          // excluded: that lead has no sequence at all and already gets its
          // own "no eligible steps" warning above.
          lead.doneAt || (visibleSteps.length > 0 && visibleSteps.every(({ i }) => i < lead.currentStep))
            ? html`
              <div class="flex items-center gap-3">
                <div
                  class="bg-success text-success-content flex h-8 w-8 items-center justify-center rounded-full text-sm"
                >
                  <span class="icon-[material-symbols--done-all] text-base"></span>
                </div>
                <span class="text-success text-sm font-medium">Sequence completed</span>
              </div>
            `
            : nothing
        }
      </div>
    `;
  };

  /** Render engagement timeline dots */
  const renderEngagement = (lead: LeadData) => {
    const isLinkedin = campaign?.campaignType === 'linkedin';
    const isMulti = campaign?.campaignType === 'multi';

    // Detect "already connected" — connectionSentAt and acceptedAt set at the same time
    const connSkipped =
      (isLinkedin || isMulti) &&
      lead.connectionSentAt &&
      lead.acceptedAt &&
      Math.abs(new Date(lead.connectionSentAt).getTime() - new Date(lead.acceptedAt).getTime()) < 5000;

    const events = isMulti
      ? [
          { label: 'Sent', date: lead.sentAt, icon: 'send', color: 'text-info' },
          { label: 'Opened', date: lead.openedAt, icon: 'drafts', color: 'text-success' },
          connSkipped
            ? { label: 'Already connected', date: lead.connectionSentAt, icon: 'how-to-reg', color: 'text-success' }
            : { label: 'Conn. Sent', date: lead.connectionSentAt, icon: 'person-add', color: 'text-info' },
          ...(!connSkipped
            ? [{ label: 'Accepted', date: lead.acceptedAt, icon: 'how-to-reg', color: 'text-success' }]
            : []),
          { label: 'DM Sent', date: lead.dmSentAt, icon: 'send', color: 'text-info' },
          { label: 'Replied', date: lead.repliedAt, icon: 'reply', color: 'text-accent' },
          { label: 'Bounced', date: lead.bouncedAt, icon: 'error', color: 'text-error' },
        ]
      : isLinkedin
        ? [
            connSkipped
              ? { label: 'Already connected', date: lead.connectionSentAt, icon: 'how-to-reg', color: 'text-success' }
              : { label: 'Conn. Sent', date: lead.connectionSentAt, icon: 'person-add', color: 'text-info' },
            ...(!connSkipped
              ? [{ label: 'Accepted', date: lead.acceptedAt, icon: 'how-to-reg', color: 'text-success' }]
              : []),
            { label: 'DM Sent', date: lead.dmSentAt, icon: 'send', color: 'text-info' },
            { label: 'Replied', date: lead.repliedAt, icon: 'reply', color: 'text-accent' },
          ]
        : [
            { label: 'Sent', date: lead.sentAt, icon: 'send', color: 'text-info' },
            { label: 'Opened', date: lead.openedAt, icon: 'drafts', color: 'text-success' },
            { label: 'Replied', date: lead.repliedAt, icon: 'reply', color: 'text-accent' },
            { label: 'Bounced', date: lead.bouncedAt, icon: 'error', color: 'text-error' },
          ];
    if (isLeadCompleted(lead)) {
      events.push({ label: 'Done', date: lead.completedAt ?? lead.repliedAt, icon: 'task-alt', color: 'text-success' });
    }
    const active = events.filter(e => e.date);
    if (active.length === 0) return html`<span class="text-base-content/40 text-xs">No engagement yet</span>`;
    return html`
      <div class="flex items-center gap-4">
        ${active.map(
          e => html`
            <div class="flex items-center gap-1">
              <span class="icon-[material-symbols--${e.icon}] ${e.color} text-sm"></span>
              <span class="${e.color} text-xs">${e.label}</span>
              <span class="text-base-content/40 text-xs">${new Date(e.date!).toLocaleDateString()}</span>
            </div>
          `,
        )}
      </div>
    `;
  };

  /**
   * Per-channel "sent" summary boxes for a single lead. Complements the inline
   * engagement line with a count + dated breakdown per channel so multi-step,
   * multi-channel sequences stay readable as they grow.
   *
   * Data sources differ by channel:
   *  - LinkedIn (connect / DM): the LinkedinJob queue is authoritative and carries
   *    per-step `stepIndex` + `executedAt`, so those boxes show an exact
   *    "Step N: <date>" line per executed step.
   *  - Email: no per-step send timestamp is stored, so the email box shows the
   *    aggregate send date(s) for the channel without step attribution.
   * Only channels with at least one actually-sent step are rendered.
   */
  const renderEngagementBoxes = (lead: LeadData) => {
    if (sequence.length === 0) return nothing;
    const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString() : '');

    const hasEmail = sequence.some(s => s.channel === 'email' || s.channel === 'email_followup');
    const hasConnect = sequence.some(s => s.channel === 'linkedin_connect');
    const hasDm = sequence.some(s => s.channel === 'linkedin_dm');

    // LinkedIn: only steps the worker actually executed count as "sent".
    const jobsForLead = linkedinJobs.filter(j => j.leadId === lead._id);
    const sentJobs = (channel: string) =>
      jobsForLead
        .filter(j => j.channel === channel && j.status === 'completed' && !!j.executedAt)
        .sort((a, b) => a.stepIndex - b.stepIndex);
    const connectSends = sentJobs('linkedin_connect');
    const dmSends = sentJobs('linkedin_dm');
    // Surfaces the ACTUAL Unipile error per lead — the campaign-level banner
    // only shows one sample across the whole campaign, which isn't enough to
    // tell whether every failing lead hit the same cause.
    const failedJobs = jobsForLead
      .filter(j => j.status === 'failed' && !!j.errorMessage)
      .sort((a, b) => (b.scheduledAt ?? '').localeCompare(a.scheduledAt ?? ''));

    // Email: real sends carry a subject; LinkedIn DMs surfaced in the same list
    // have an empty subject, so the subject filter keeps emails only. Falls back
    // to the aggregate lead.sentAt when the provider list lags or isn't loaded.
    const isExpanded = expandedLeadId === lead._id;
    const emailDates = (isExpanded ? leadEmails : [])
      .filter(e => e.type === 'sent' && (e.subject ?? '').trim().length > 0)
      .map(e => e.date)
      .filter((d): d is string => !!d)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const emailSends = emailDates.length > 0 ? emailDates : lead.sentAt ? [lead.sentAt] : [];

    const line = (text: string) => html`<span class="text-base-content/50 text-xs">${text}</span>`;
    const box = (label: string, icon: string, count: number, lines: unknown[]) => html`
      <div class="card bg-base-100 min-w-35 shadow-sm">
        <div class="card-body gap-1 p-3">
          <div class="flex items-center gap-1.5">
            <span class="icon-[material-symbols--${icon}] text-info text-base"></span>
            <span class="text-base-content/70 text-xs font-medium">${label}</span>
          </div>
          <p class="text-2xl leading-none font-bold">${count}</p>
          <span class="text-base-content/40 text-xs tracking-wide uppercase">Sent</span>
          <div class="mt-1 flex flex-col gap-0.5">${lines}</div>
        </div>
      </div>
    `;

    const boxes: unknown[] = [];
    if (hasEmail && emailSends.length > 0)
      boxes.push(
        box(
          'Email',
          'mail',
          emailSends.length,
          emailSends.map(d => line(`Sent ${fmt(d)}`)),
        ),
      );
    if (hasConnect && connectSends.length > 0)
      boxes.push(
        box(
          'Connection Request',
          'person-add',
          connectSends.length,
          connectSends.map(j => line(`Step ${j.stepIndex + 1}: ${fmt(j.executedAt)}`)),
        ),
      );
    if (hasDm && dmSends.length > 0)
      boxes.push(
        box(
          'LinkedIn DM',
          'chat',
          dmSends.length,
          dmSends.map(j => line(`Step ${j.stepIndex + 1}: ${fmt(j.executedAt)}`)),
        ),
      );

    if (boxes.length === 0 && failedJobs.length === 0) return nothing;
    return html`
      <div class="mt-3 flex flex-wrap gap-3">
        ${boxes}
        ${
          failedJobs.length > 0
            ? html`
              <div class="card bg-error/10 min-w-35 shadow-sm">
                <div class="card-body gap-1 p-3">
                  <div class="flex items-center gap-1.5">
                    <span class="icon-[material-symbols--error-outline] text-error text-base"></span>
                    <span class="text-error text-xs font-medium">LinkedIn error</span>
                  </div>
                  <span class="text-base-content/70 text-xs break-words">${failedJobs[0]?.errorMessage}</span>
                </div>
              </div>
            `
            : nothing
        }
      </div>
    `;
  };

  /** Render expanded lead detail (accordion panel) */
  const renderLeadDetail = (lead: LeadData) => {
    // Whether this lead has an email step at all — the difference between
    // "nothing was sent" and "the provider has not told us yet".
    const hasEmailStep =
      (campaign?.sequence ?? []).some(
        (s: { channel: string }) => s.channel === 'email' || s.channel === 'email_followup',
      ) && !!(lead.email ?? '').trim();
    // Why this lead's DM did not go out on its own. Stated in the expansion
    // because that is where the operator has already decided to look at THIS
    // lead — and where the button that overrides it sits. A chip in the table
    // can say "skipped"; only here is there room to say what to do about it.
    const withheld = lead.alreadyConnectedNoticeDismissedAt
      ? null
      : dmWithheldNotice({
          alreadyConnected: lead.alreadyConnected,
          dmSentAt: lead.dmSentAt,
          campaignHasDmStep,
        });
    return html`
    <tr class="ndi-expand">
      <td colspan="10" class="bg-base-200/50 p-0">
        <div class="sticky left-0 w-[100cqw] flex flex-col gap-4 p-4">
          ${
            withheld
              ? html`
                <div class="alert alert-warning py-2">
                  <span class="icon-[material-symbols--link] text-lg" aria-hidden="true"></span>
                  <span class="text-sm flex-1">${withheld.text} <strong>${withheld.action}</strong></span>
                  <button
                    class="btn btn-ghost btn-xs btn-circle"
                    title="Dismiss this notice — it stays hidden for this lead"
                    aria-label="Dismiss notice"
                    @click=${() => dismissConnectedNotice(lead)}
                  >
                    <span class="icon-[material-symbols--close] text-base" aria-hidden="true"></span>
                  </button>
                </div>
              `
              : nothing
          }
          <!-- Lead Info + Engagement -->
          <div class="flex flex-wrap items-start gap-6">
            <div class="flex flex-col gap-1">
              <h3 class="font-semibold">${lead.firstName} ${lead.lastName}</h3>
              <span class="text-base-content/70 text-sm">${lead.email}</span>
              <span class="text-base-content/70 text-sm"
                >${lead.jobTitle}${lead.company ? ` at ${lead.company}` : ''}</span
              >
              ${
                lead.linkedinUrl
                  ? html`<a href="${lead.linkedinUrl}" target="_blank" rel="noopener" class="link link-primary text-sm"
                    >${abbreviateUrl(lead.linkedinUrl)}</a
                  >`
                  : nothing
              }
              ${
                // Who sent to this lead, and from WHICH account. One rep can
                // have several mailboxes or LinkedIn seats, and "Sent by <rep>"
                // alone could not say which one actually sent. The account name
                // is the mailbox ADDRESS for email, the person's name for
                // LinkedIn. Falls back to just "Sent by <rep>" when no account
                // resolves (nothing sent yet), and shows nothing when neither is
                // known.
                (() => {
                  const rep = lead.aiEmployeeId ? salesRepsById[lead.aiEmployeeId] : undefined;
                  const acct = resolveLeadSendingAccount(linkedinJobs, lead._id, accountNames);
                  if (!rep && !acct) return nothing;
                  const using = acct
                    ? html`using <span class="font-medium">${acct.name}</span> Account`
                    : nothing;
                  return html`<span class="text-base-content/70 text-sm"
                    >${rep ? html`Sent by ${rep} ${using}` : html`Sent ${using}`}</span
                  >`;
                })()
              }
            </div>
            <div class="flex flex-1 flex-col gap-1">
              <span class="text-base-content/60 text-xs font-medium uppercase">Engagement</span>
              ${renderEngagement(lead)} ${renderEngagementBoxes(lead)}
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <!-- Step Timeline -->
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body py-3">
                <h4 class="text-sm font-semibold">Sequence Progress</h4>
                ${renderStepTimeline(lead)}
                ${
                  // Only for campaigns that actually run through the LinkedIn
                  // job queue — email steps go out via Instantly and have no
                  // equivalent "send this one now" handle.
                  campaign?.campaignType === 'linkedin' || campaign?.campaignType === 'multi'
                    ? (
                        () => {
                          const blocked = sendNowBlockedReason(lead);
                          const busy = sendingNextStepLeadId === lead._id;
                          return html`
                          <div class="border-base-300 mt-2 flex flex-wrap items-center gap-2 border-t pt-2">
                            <button
                              class="btn btn-outline btn-xs gap-1.5 ${blocked || busy ? 'btn-disabled' : ''}"
                              title=${blocked ?? 'Send this lead’s next step now instead of waiting for its delay'}
                              @click=${() => {
                                if (blocked || busy) return;
                                void sendNextStepNow(lead);
                              }}
                            >
                              ${
                                busy
                                  ? html`<span class="loading loading-spinner loading-xs"></span>`
                                  : html`<span class="icon-[material-symbols--bolt] text-sm"></span>`
                              }
                              ${
                                // Name the step, so the operator knows what
                                // they are about to send rather than trusting
                                // "next". For a lead whose connect step was
                                // skipped, this reads "Send connection request
                                // now" — which is precisely the action that
                                // was previously refused.
                                lead.nextStep?.channel === 'linkedin_connect'
                                  ? 'Send connection request now'
                                  : lead.nextStep?.channel === 'linkedin_dm'
                                    ? 'Send DM now'
                                    : 'Send Now'
                              }
                            </button>
                            <span class="text-base-content/50 text-xs">
                              ${
                                blocked ??
                                `Step ${lead.nextStep?.stepNumber ?? '?'} — skips the waiting time, goes out within about a minute.`
                              }
                            </span>
                          </div>
                        `;
                        }
                      )()
                    : nothing
                }
                <!-- Manual status is set by clicking the status chip in the lead
                     row (leadStatusChipMenu); the duplicate control that used to
                     live here was removed. -->
              </div>
            </div>

            <!-- Messages -->
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body py-3">
                <h4 class="text-sm font-semibold">Messages</h4>
                ${
                  loadingEmails
                    ? html`<div class="flex justify-center py-4">
                      <span class="loading loading-spinner loading-sm"></span>
                    </div>`
                    : leadEmails.length === 0
                      ? html`<p class="text-base-content/50 py-2 text-sm">
                        <!--
                          "No messages yet" asserted that nothing had been sent.
                          For email we have no local send record at all — the
                          provider does the sending and reports it afterwards,
                          with a delay. A lead whose email had already arrived in
                          the recipient's inbox therefore read "No messages yet",
                          which is not a cautious statement, it is a wrong one.
                          Absence of a report is not a report of absence.
                        -->
                        ${
                          lead.replyText
                            ? 'No email history available from provider'
                            : !hasEmailStep
                              ? 'No messages yet'
                              : instantlyInvolved
                                ? 'Instantly has not reported any email for this lead yet. It reports sends with a delay, so this is not proof that nothing was sent.'
                                : 'No email has been sent to this lead yet. This campaign sends through the scheduler, which records every send as it happens — so unlike the Instantly path, this is not a reporting delay.'
                        }
                      </p>`
                      : html`
                        <div class="flex max-h-72 flex-col gap-3 overflow-y-auto">
                          ${leadEmails.map(
                            e => html`
                              <div
                                class="${
                                  e.type === 'received'
                                    ? 'border-l-2 border-success bg-success/5'
                                    : e.simulated
                                      ? 'border-l-2 border-warning bg-warning/5'
                                      : 'border-l-2 border-info bg-info/5'
                                } rounded-r p-3"
                              >
                                <div class="mb-1 flex items-center gap-2">
                                  <span
                                    class="badge badge-xs ${
                                      e.type === 'received'
                                        ? 'badge-success'
                                        : e.simulated
                                          ? 'badge-warning'
                                          : 'badge-info'
                                    }"
                                    >${
                                      e.type === 'received'
                                        ? 'Reply'
                                        : e.simulated
                                          ? e.channel === 'linkedin_connect'
                                            ? 'Would send · Connect'
                                            : 'Would send'
                                          : 'Sent'
                                    }</span
                                  >
                                  ${e.subject ? html`<span class="text-sm font-medium">${e.subject}</span>` : nothing}
                                  <span class="text-base-content/40 ml-auto text-xs"
                                    >${e.date ? new Date(e.date).toLocaleString() : ''}</span
                                  >
                                </div>
                                ${
                                  e.simulated
                                    ? html`<p class="text-warning/80 mb-1 text-xs">
                                      Simulated — not actually sent (dev mode)
                                    </p>`
                                    : nothing
                                }
                                <p class="text-base-content/80 text-sm whitespace-pre-wrap">${e.body}</p>
                              </div>
                            `,
                          )}
                        </div>
                      `
                }
                ${
                  lead.replyMessages && lead.replyMessages.length > 0 && leadEmails.every(e => e.type !== 'received')
                    ? html`
                      ${lead.replyMessages.map(
                        rm => html`
                          <div class="border-success bg-success/5 mt-2 rounded-r border-l-2 p-3">
                            <div class="mb-1 flex items-center gap-2">
                              <span class="badge badge-success badge-xs">Reply</span>
                              <span class="text-base-content/40 ml-auto text-xs"
                                >${rm.timestamp ? new Date(rm.timestamp).toLocaleString() : ''}</span
                              >
                            </div>
                            <p class="text-base-content/80 text-sm whitespace-pre-wrap">${rm.text}</p>
                          </div>
                        `,
                      )}
                    `
                    : lead.replyText && leadEmails.every(e => e.type !== 'received')
                      ? html`
                        <div class="border-success bg-success/5 mt-2 rounded-r border-l-2 p-3">
                          <div class="mb-1 flex items-center gap-2">
                            <span class="badge badge-success badge-xs">Reply</span>
                            <span class="text-base-content/40 ml-auto text-xs"
                              >${lead.repliedAt ? new Date(lead.repliedAt).toLocaleString() : ''}</span
                            >
                          </div>
                          <p class="text-base-content/80 text-sm whitespace-pre-wrap">${lead.replyText}</p>
                        </div>
                      `
                      : nothing
                }
              </div>
            </div>
          </div>

          <!-- Per-Lead Generated Messages (pre-launch review/edit) -->
          ${(() => {
            const leadMessages = messages
              .filter(m => m.leadId === lead._id)
              .slice()
              .sort((a, b) => a.stepIndex - b.stepIndex);
            // A lead with nothing generated used to render `nothing` — the card
            // vanished, and the screen built for reviewing a message before it
            // reaches a real person said nothing at all about there being none.
            // The operator's only remaining route was the bulk send.
            const seqChannels = new Set((campaign.sequence ?? []).map((s: { channel?: string }) => s.channel));
            const emptyNotice = emptyMessagesNotice({
              messageCount: leadMessages.length,
              campaignStatus: campaign.status,
              leadHasEmail: !!(lead.email ?? '').trim(),
              leadHasLinkedin: !!(lead.linkedinUrl ?? '').trim(),
              campaignUsesEmail: seqChannels.has('email') || seqChannels.has('email_followup'),
              campaignUsesLinkedin: seqChannels.has('linkedin_connect') || seqChannels.has('linkedin_dm'),
              leadOptedOut: lead.status === 'opted_out',
            });
            if (emptyNotice) {
              return html`
                <div class="card bg-base-100 shadow-sm">
                  <div class="card-body py-3">
                    <div
                      class="flex items-start gap-2 text-sm ${emptyNotice.kind === 'gap' ? 'text-warning' : 'opacity-70'}"
                    >
                      <span
                        class="icon-[material-symbols--${emptyNotice.kind === 'gap'
                          ? 'warning-outline'
                          : 'hourglass-top'}] mt-0.5 text-base"
                      ></span>
                      <div>
                        <div class="font-semibold">${emptyNotice.title}</div>
                        <div class="opacity-80">${emptyNotice.detail}</div>
                      </div>
                    </div>
                  </div>
                </div>
              `;
            }
            const editable = isPreLaunchEditable(campaign.status);
            // Pre-launch (generating / ready_to_launch / preparing_to_launch): always expanded so user can review/edit.
            // Post-launch: collapsed by default — user can expand on demand to inspect each message.
            const isExpanded = editable || expandedMessagesLeadIds.has(lead._id);
            const toggleExpanded = () => {
              const next = new Set(expandedMessagesLeadIds);
              if (next.has(lead._id)) next.delete(lead._id);
              else next.add(lead._id);
              setExpandedMessagesLeadIds(next);
            };
            const channelLabels: Record<string, string> = {
              email: 'Email',
              email_followup: 'Follow-up Email',
              linkedin_connect: 'LinkedIn Connect',
              linkedin_dm: 'LinkedIn DM',
            };
            return html`
              <div class="card bg-base-100 shadow-sm">
                <div class="card-body py-3">
                  <div
                    class="${editable ? '' : 'cursor-pointer select-none'} flex items-center gap-1"
                    @click=${editable ? undefined : toggleExpanded}
                  >
                    <h4 class="flex flex-1 items-center gap-1 text-sm font-semibold">
                      <span class="icon-[material-symbols--auto-awesome] text-base"></span>
                      Generated Messages
                      <span class="badge badge-ghost badge-xs">${leadMessages.length}</span>
                      ${
                        !editable
                          ? html`<span class="badge badge-ghost badge-xs">read-only</span>`
                          : html`<span class="text-base-content/40 text-xs font-normal">(Preview)</span>`
                      }
                    </h4>
                    ${
                      !editable
                        ? html`<button
                          class="btn btn-ghost btn-xs btn-square"
                          title=${isExpanded ? 'Collapse' : 'Expand'}
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            toggleExpanded();
                          }}
                        >
                          <span
                            class="icon-[material-symbols--${isExpanded ? 'expand-less' : 'expand-more'}] text-lg"
                          ></span>
                        </button>`
                        : nothing
                    }
                  </div>
                  ${
                    isExpanded
                      ? html`<div class="mt-2 space-y-3">
                        ${leadMessages.map(m => {
                          const isEditing = editingMessageId === m._id;
                          const isSaving = savingMessageId === m._id;
                          const isRegenerating = regeneratingMessageId === m._id;
                          const channelLabel = channelLabels[m.channel] ?? m.channel;
                          const showSubject = m.channel === 'email' || m.channel === 'email_followup';

                          // Deferred messages — generated at runtime from reply, not editable
                          if (m.generationStatus === 'deferred') {
                            return html`
                              <div class="border-base-300 bg-base-200/30 rounded-lg border p-3">
                                <div class="mb-1 flex items-center gap-2">
                                  <span class="badge badge-sm badge-outline">Step ${m.stepIndex + 1}</span>
                                  <span class="text-sm font-medium">${channelLabel}</span>
                                  <span class="badge badge-info badge-sm badge-outline ml-auto"
                                    >Generated at runtime from reply</span
                                  >
                                </div>
                                <p class="text-base-content/60 text-xs">
                                  This step replies to the lead's response and will be generated automatically once the
                                  lead replies.
                                </p>
                              </div>
                            `;
                          }

                          // Failed
                          if (m.generationStatus === 'failed') {
                            return html`
                              <div class="border-error bg-error/5 rounded-lg border p-3">
                                <div class="mb-1 flex items-center gap-2">
                                  <span class="badge badge-sm badge-error">Step ${m.stepIndex + 1}</span>
                                  <span class="text-sm font-medium">${channelLabel}</span>
                                  <span class="badge badge-error badge-sm ml-auto">Failed</span>
                                </div>
                                <p class="text-error/80 mt-1 text-sm">${m.generationError ?? 'Generation failed'}</p>
                                ${
                                  editable
                                    ? html`
                                      <div class="mt-2 flex justify-end">
                                        <button
                                          class="btn btn-sm btn-outline ${isRegenerating ? 'btn-disabled' : ''} gap-1"
                                          @click=${() => regenerateMessage(m._id)}
                                        >
                                          ${
                                            isRegenerating
                                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                                              : html`<span class="icon-[material-symbols--autorenew]"></span>`
                                          }
                                          Regenerate
                                        </button>
                                      </div>
                                    `
                                    : nothing
                                }
                              </div>
                            `;
                          }

                          // Pending / generating
                          //
                          // QA #4: 'Pending' with a spinner reads as "something
                          // is happening", and for a step added in the wizard
                          // nothing is: the queue is drained once, when the user
                          // LEAVES the wizard. A tester watched a spinner that
                          // was never going to stop and reported it as broken.
                          // 'generating' really is in flight and keeps the
                          // spinner; 'pending' says what it is waiting for.
                          if (m.generationStatus !== 'ready') {
                            const inFlight = m.generationStatus === 'generating';
                            return html`
                              <div class="border-base-300 bg-base-200/30 rounded-lg border p-3">
                                <div class="flex items-center gap-2">
                                  <span class="badge badge-sm badge-outline">Step ${m.stepIndex + 1}</span>
                                  <span class="text-sm font-medium">${channelLabel}</span>
                                  <span class="badge badge-info badge-sm ml-auto gap-1">
                                    ${inFlight ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
                                    ${inFlight ? 'Generating...' : 'Not written yet'}
                                  </span>
                                </div>
                                ${
                                  inFlight
                                    ? nothing
                                    : html`
                                      <p class="text-base-content/60 mt-1 text-xs">
                                        This step is written when you leave the campaign editor — everything you changed
                                        is generated in one pass.
                                      </p>
                                    `
                                }
                              </div>
                            `;
                          }

                          // Ready (default)
                          return html`
                            <div class="border-base-300 rounded-lg border p-3">
                              <div class="mb-2 flex items-center gap-2">
                                <span class="badge badge-sm badge-outline">Step ${m.stepIndex + 1}</span>
                                <span class="text-sm font-medium">${channelLabel}</span>
                                ${m.edited ? html`<span class="badge badge-info badge-xs">edited</span>` : nothing}
                                <span class="text-base-content/40 ml-auto text-xs"
                                  >${m.generatedAt ? new Date(m.generatedAt).toLocaleString() : ''}</span
                                >
                              </div>
                              ${
                                isEditing
                                  ? html`
                                    <div class="ndi-expand flex flex-col gap-1">
                                      ${
                                        showSubject
                                          ? html`
                                            <label class="label py-1"
                                              ><span class="label-text text-xs font-medium">Subject</span></label
                                            >
                                            <input
                                              class="input input-sm input-bordered w-full"
                                              .value=${editSubjectDraft}
                                              @input=${(e: Event) =>
                                                setEditSubjectDraft((e.target as HTMLInputElement).value)}
                                            />
                                          `
                                          : nothing
                                      }
                                      <label class="label mt-1 py-1"
                                        ><span class="label-text text-xs font-medium">Body</span></label
                                      >
                                      <textarea
                                        class="textarea textarea-bordered w-full text-sm"
                                        rows="6"
                                        .value=${editBodyDraft}
                                        @input=${(e: Event) =>
                                          setEditBodyDraft((e.target as HTMLTextAreaElement).value)}
                                      ></textarea>
                                      <div class="mt-2 flex justify-end gap-2">
                                        <button
                                          class="btn btn-ghost btn-xs"
                                          @click=${cancelEditMessage}
                                          ?disabled=${isSaving}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          class="btn btn-primary btn-xs gap-1"
                                          @click=${() => saveEditMessage(m._id)}
                                          ?disabled=${isSaving}
                                        >
                                          ${
                                            isSaving
                                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                                              : html`<span class="icon-[material-symbols--save]"></span>`
                                          }
                                          Save
                                        </button>
                                      </div>
                                    </div>
                                  `
                                  : html`
                                    ${
                                      showSubject && m.subject
                                        ? html`
                                          <div class="mb-2">
                                            <span class="text-base-content/50 text-xs font-medium">Subject</span>
                                            <p class="text-sm font-medium">${m.subject}</p>
                                          </div>
                                        `
                                        : nothing
                                    }
                                    <div>
                                      <span class="text-base-content/50 text-xs font-medium">Body</span>
                                      <p class="mt-1 text-sm whitespace-pre-wrap">${m.body}</p>
                                    </div>
                                    ${
                                      editable
                                        ? html`
                                          <div class="mt-2 flex justify-end gap-2">
                                            <button
                                              class="btn btn-ghost btn-xs ${isRegenerating ? 'btn-disabled' : ''} gap-1"
                                              @click=${() => regenerateMessage(m._id)}
                                            >
                                              ${
                                                isRegenerating
                                                  ? html`<span class="loading loading-spinner loading-xs"></span>`
                                                  : html`<span class="icon-[material-symbols--autorenew]"></span>`
                                              }
                                              Regenerate
                                            </button>
                                            <button
                                              class="btn btn-ghost btn-xs gap-1"
                                              @click=${() => startEditMessage(m)}
                                            >
                                              <span class="icon-[material-symbols--edit]"></span> Edit
                                            </button>
                                          </div>
                                        `
                                        : nothing
                                    }
                                  `
                              }
                            </div>
                          `;
                        })}
                      </div>`
                      : nothing
                  }
                </div>
              </div>
            `;
          })()}

          <!-- Per-Lead Reply Drafts -->
          ${(() => {
            if (!campaign?.autoReply) return nothing;
            const leadDrafts = drafts.filter(d => d.leadId === lead._id);
            if (leadDrafts.length === 0 && !lead.repliedAt) return nothing;
            const pendingLeadDrafts = leadDrafts.filter(d => d.status === 'pending_review');
            const intentColors: Record<string, string> = {
              opt_out: 'badge-error',
              interested: 'badge-success',
              positive: 'badge-success',
              question: 'badge-info',
              objection: 'badge-warning',
              neutral: 'badge-ghost',
              off_topic: 'badge-ghost',
            };
            const sentimentIcons: Record<string, string> = {
              positive: 'icon-[material-symbols--sentiment-satisfied]',
              neutral: 'icon-[material-symbols--sentiment-neutral]',
              negative: 'icon-[material-symbols--sentiment-dissatisfied]',
            };
            const statusClasses: Record<string, string> = {
              pending_review: 'border-warning',
              approved: 'border-success',
              queued: 'border-info',
              send_failed: 'border-error',
              rejected: 'border-error',
              sent: 'border-success/50',
              edited: 'border-info',
            };
            return html`
              <div class="card bg-base-100 shadow-sm">
                <div class="card-body py-3">
                  <h4 class="flex items-center gap-1 text-sm font-semibold">
                    <span class="icon-[material-symbols--reply] text-base"></span>
                    Reply Drafts
                    ${
                      pendingLeadDrafts.length > 0
                        ? html`<span class="badge badge-warning badge-xs">${pendingLeadDrafts.length} pending</span>`
                        : nothing
                    }
                  </h4>

                  <!-- Draft Cards -->
                  ${
                    leadDrafts.length === 0
                      ? html`<p class="text-base-content/50 py-2 text-sm">No drafts yet.</p>`
                      : html`
                        <div class="mt-2 space-y-3">
                          ${leadDrafts.map(d => {
                            const isExpanded = expandedDraftId === d._id;
                            const isEditing = editingDraftId === d._id;
                            const isPending = d.status === 'pending_review';
                            const isApproving = approvingDraftId === d._id;
                            const contextOpen = expandedContextDraftId === d._id;

                            return html`
                              <div class="${statusClasses[d.status] ?? 'border-base-300'} rounded-lg border p-3">
                                <!-- Collapsed Header -->
                                <div
                                  class="flex cursor-pointer items-center justify-between"
                                  @click=${() => setExpandedDraftId(isExpanded ? null : d._id)}
                                >
                                  <div class="flex items-center gap-2">
                                    <span
                                      class="badge badge-sm ${
                                        intentColors[d.classifyResult.replyIntent] ?? 'badge-ghost'
                                      }"
                                      >${d.classifyResult.replyIntent}</span
                                    >
                                    <span
                                      class="${sentimentIcons[d.classifyResult.sentiment] ?? ''} text-base-content/60"
                                    ></span>
                                    ${
                                      !d.classifyResult.promptActionAppropriate
                                        ? html`<span class="badge badge-error badge-sm badge-outline">CTA skipped</span>`
                                        : nothing
                                    }
                                  </div>
                                  <div class="flex items-center gap-2">
                                    ${
                                      d.status === 'sent'
                                        ? html`<span class="badge badge-success badge-sm">Sent</span>`
                                        : d.status === 'queued'
                                          ? html`<span class="badge badge-info badge-sm">DM Scheduled</span>`
                                          : d.status === 'send_failed'
                                            ? html`<span class="badge badge-error badge-sm">Send Failed</span>`
                                            : d.status === 'approved'
                                              ? html`<span class="badge badge-success badge-sm badge-outline">Approved</span>`
                                              : d.status === 'rejected'
                                                ? html`<span class="badge badge-error badge-sm badge-outline">Rejected</span>`
                                                : html`<span class="badge badge-warning badge-sm">Pending</span>`
                                    }
                                    <span class="text-base-content/40 text-xs"
                                      >${new Date(d.generatedAt).toLocaleString()}</span
                                    >
                                    <span
                                      class="icon-[material-symbols--${
                                        isExpanded ? 'expand-less' : 'expand-more'
                                      }] text-base-content/50 text-lg"
                                    ></span>
                                  </div>
                                </div>

                                ${
                                  isExpanded
                                    ? html`
                                      <div class="ndi-expand mt-3 space-y-3">
                                        <!--
                                          Reply-first: the draft the operator acts on
                                          leads, with its actions right beneath it. The
                                          surrounding context (reasoning, the thread,
                                          the lead's reply) sits collapsed below.
                                        -->
                                        <!-- Draft Message -->
                                        <div class="bg-primary/10 rounded p-2">
                                          <div class="text-primary mb-1 text-xs font-semibold">
                                            Draft Response ${d.editedMessage ? '(edited)' : ''}
                                          </div>
                                          ${
                                            isEditing
                                              ? html`
                                                <textarea
                                                  class="textarea textarea-bordered w-full text-sm"
                                                  rows="4"
                                                  .value=${editDraftMessage}
                                                  @input=${(e: Event) =>
                                                    setEditDraftMessage((e.target as HTMLTextAreaElement).value)}
                                                ></textarea>
                                                <div class="mt-2 flex gap-2">
                                                  <button
                                                    class="btn btn-primary btn-xs"
                                                    @click=${() => saveDraftEdit(d._id)}
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    class="btn btn-ghost btn-xs"
                                                    @click=${() => setEditingDraftId(null)}
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              `
                                              : html`
                                                <p class="text-sm whitespace-pre-wrap">${
                                                  d.editedMessage || d.draftMessage
                                                }</p>
                                              `
                                          }
                                        </div>

                                        <!-- Actions -->
                                        ${
                                          isPending
                                            ? html`
                                              <div class="flex flex-wrap justify-end gap-2">
                                                <button
                                                  class="btn btn-ghost btn-xs"
                                                  @click=${(e: Event) => {
                                                    e.stopPropagation();
                                                    regenerateDraft(d._id);
                                                  }}
                                                  ?disabled=${isApproving}
                                                >
                                                  <span class="icon-[material-symbols--autorenew]"></span> Regenerate
                                                </button>
                                                <button
                                                  class="btn btn-ghost btn-xs"
                                                  @click=${(e: Event) => {
                                                    e.stopPropagation();
                                                    setEditingDraftId(d._id);
                                                    setEditDraftMessage(d.editedMessage || d.draftMessage);
                                                  }}
                                                >
                                                  <span class="icon-[material-symbols--edit]"></span> Edit
                                                </button>
                                                <button
                                                  class="btn btn-error btn-xs btn-outline"
                                                  @click=${(e: Event) => {
                                                    e.stopPropagation();
                                                    rejectDraft(d._id);
                                                  }}
                                                >
                                                  <span class="icon-[material-symbols--close]"></span> Reject
                                                </button>
                                                ${
                                                  campaign?.campaignType === 'multi'
                                                    ? html`
                                                      <button
                                                        class="btn btn-primary btn-xs"
                                                        @click=${(e: Event) => {
                                                          e.stopPropagation();
                                                          approveDraft(d._id, 'email');
                                                        }}
                                                        ?disabled=${isApproving}
                                                      >
                                                        ${
                                                          isApproving
                                                            ? html`<span
                                                              class="loading loading-spinner loading-xs"
                                                            ></span>`
                                                            : html`<span class="icon-[material-symbols--mail]"></span>`
                                                        }
                                                        Send with Email
                                                      </button>
                                                      <button
                                                        class="btn btn-secondary btn-xs"
                                                        @click=${(e: Event) => {
                                                          e.stopPropagation();
                                                          approveDraft(d._id, 'linkedin_dm');
                                                        }}
                                                        ?disabled=${isApproving}
                                                      >
                                                        ${
                                                          isApproving
                                                            ? html`<span
                                                              class="loading loading-spinner loading-xs"
                                                            ></span>`
                                                            : html`<span class="icon-[material-symbols--share]"></span>`
                                                        }
                                                        Send with LinkedIn
                                                      </button>
                                                    `
                                                    : html`
                                                      <button
                                                        class="btn btn-success btn-xs"
                                                        @click=${(e: Event) => {
                                                          e.stopPropagation();
                                                          approveDraft(d._id);
                                                        }}
                                                        ?disabled=${isApproving}
                                                      >
                                                        ${
                                                          isApproving
                                                            ? html`<span
                                                              class="loading loading-spinner loading-xs"
                                                            ></span>`
                                                            : html`<span class="icon-[material-symbols--check]"></span>`
                                                        }
                                                        Approve & Send
                                                      </button>
                                                    `
                                                }
                                              </div>
                                            `
                                            : nothing
                                        }

                                        <!-- Context — collapsed by default -->
                                        <div class="border-base-300 border-t pt-2">
                                          <button
                                            class="btn btn-ghost btn-xs text-base-content/60 gap-1"
                                            @click=${(e: Event) => {
                                              e.stopPropagation();
                                              setExpandedContextDraftId(contextOpen ? null : d._id);
                                            }}
                                          >
                                            <span
                                              class="icon-[material-symbols--${
                                                contextOpen ? 'expand-less' : 'expand-more'
                                              }] text-base"
                                            ></span>
                                            ${contextOpen ? 'Hide context' : 'Expand context'}
                                          </button>
                                          ${
                                            contextOpen
                                              ? html`
                                                <div class="mt-2 space-y-3">
                                                  <!-- Agent Reasoning -->
                                                  <div class="bg-base-200/50 rounded p-2">
                                                    <div class="text-base-content/60 mb-1 text-xs font-semibold">
                                                      Agent Reasoning
                                                    </div>
                                                    <p class="text-sm">${d.classifyResult.reasoning}</p>
                                                    <div class="text-base-content/40 mt-1 text-xs">
                                                      Strategy: ${d.classifyResult.suggestedStrategy}
                                                    </div>
                                                  </div>

                                                  <!-- Conversation History -->
                                                  ${
                                                    d.conversationHistory.length > 0
                                                      ? html`
                                                        <div>
                                                          <div class="text-base-content/60 mb-1 text-xs font-semibold">
                                                            Conversation
                                                          </div>
                                                          <div class="space-y-1.5">
                                                            ${d.conversationHistory.map(
                                                              m => html`
                                                                <div class="flex flex-col gap-0.5 text-sm">
                                                                  <span
                                                                    class="${
                                                                      m.role === 'agent' ? 'text-primary' : 'text-secondary'
                                                                    } font-medium"
                                                                    >${m.role === 'agent' ? 'You' : 'Lead'}:</span
                                                                  >
                                                                  <span
                                                                    class="text-base-content/80 break-words whitespace-pre-wrap"
                                                                    >${m.role === 'agent' ? m.message : replyDisplayText(m.message)}</span
                                                                  >
                                                                </div>
                                                              `,
                                                            )}
                                                          </div>
                                                        </div>
                                                      `
                                                      : nothing
                                                  }

                                                  <!-- Customer Reply -->
                                                  <div class="bg-secondary/10 rounded p-2">
                                                    <div class="text-secondary mb-1 text-xs font-semibold">Lead's Reply</div>
                                                    <p class="text-sm whitespace-pre-wrap">${replyDisplayText(d.customerReply)}</p>
                                                  </div>
                                                </div>
                                              `
                                              : nothing
                                          }
                                        </div>
                                      </div>
                                    `
                                    : nothing
                                }
                              </div>
                            `;
                          })}
                        </div>
                      `
                  }
                </div>
              </div>
            `;
          })()}
        </div>
      </td>
    </tr>
  `;
  };

  return html`
    <div class="mx-auto flex max-w-6xl flex-col gap-6 p-6">
      <!-- Header -->
      <div class="flex flex-col gap-4">
        <nav class="text-base-content/60 flex items-center gap-1 text-sm" aria-label="Breadcrumb">
          <a class="link link-hover" @click=${() => sdrRouter.goTo('dashboard')}>Dashboard</a>
          <span aria-hidden="true">›</span>
          <span class="text-base-content/80 max-w-xs truncate font-medium" aria-current="page"
            >${campaign.name || 'Untitled campaign'}</span
          >
        </nav>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <button
              class="btn btn-ghost btn-sm btn-square"
              @click=${() => sdrRouter.goTo('dashboard')}
              aria-label="Back to dashboard"
              title="Back to dashboard"
            >
              <span class="icon-[material-symbols--arrow-back] text-lg" aria-hidden="true"></span>
            </button>
            <h1 class="text-3xl font-bold">${campaign.name || 'Untitled'}</h1>
            <button
              class="btn btn-ghost btn-xs btn-square"
              title="Edit campaign name and description"
              aria-label="Edit campaign name and description"
              @click=${startEditMeta}
            >
              <span class="icon-[material-symbols--edit] text-base"></span>
            </button>
            ${statusBadge(campaign.status)}
            ${
              campaign.campaignType === 'email'
                ? html`<span class="badge badge-outline gap-1"
                  ><span class="icon-[material-symbols--mail] text-sm"></span> Email</span
                >`
                : campaign.campaignType === 'linkedin'
                  ? html`<span class="badge badge-outline gap-1"
                    ><span class="icon-[material-symbols--share] text-sm"></span> LinkedIn</span
                  >`
                  : html`<span class="badge badge-outline gap-1"
                    ><span class="icon-[material-symbols--hub] text-sm"></span> Cross-Channel</span
                  >`
            }
          </div>
          ${
            campaign.description
              ? html`<p class="text-base-content/70 max-w-2xl text-sm whitespace-pre-wrap">
                ${campaign.description}
              </p>`
              : nothing
          }
          <div class="text-base-content/50 flex gap-4 text-sm">
            ${
              campaign.launchedAt
                ? html`<span>Launched: ${new Date(campaign.launchedAt).toLocaleDateString()}</span>`
                : nothing
            }
            ${
              campaign.lastSyncedAt
                ? html`<span>Last synced: ${new Date(campaign.lastSyncedAt).toLocaleString()}</span>`
                : nothing
            }
            <span>Created: ${new Date(campaign.createdAt).toLocaleDateString()}</span>
          </div>
          <div class="mt-1 flex items-center gap-2">
            <span class="text-base-content/50 flex items-center gap-1 text-sm">
              <span class="icon-[material-symbols--folder] text-base"></span> Project:
            </span>
            <select
              class="select select-bordered select-xs"
              ?disabled=${movingProject}
              @change=${(e: Event) => void moveToProject((e.target as HTMLSelectElement).value)}
            >
              ${projects
                .slice()
                .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : a.name.localeCompare(b.name)))
                .map(p => {
                  const current = campaign.projectId ?? projects.find(pr => pr.isDefault)?._id;
                  return html`
                    <option value=${p._id} ?selected=${p._id === current}>
                      ${p.isDefault ? 'Default Project' : p.name}
                    </option>
                  `;
                })}
            </select>
            <span class="group text-base-content/40 relative">
              <span class="icon-[material-symbols--info-outline] text-base"></span>
              <span
                class="border-base-300 bg-base-100 pointer-events-none absolute top-full left-0 z-20 mt-1 hidden w-64 flex-col gap-0.5 rounded-lg border p-2 text-left text-xs shadow-lg group-hover:flex"
              >
                <span
                  >Only the Default Project allows the same lead to appear in more than one campaign. Moving a
                  campaign into any other project that already has one of its leads will pause it automatically so
                  you can review before it resumes.</span
                >
              </span>
            </span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          ${
            campaign.status === 'draft' ||
            campaign.status === 'ready_to_launch' ||
            campaign.status === 'preparing_to_launch' ||
            // 'paused' is the editing window for a campaign that has already
            // launched: pause, Edit, change what you need, resume. Leads, the
            // Offering and the Sequence all accept it (UAT E6) — the one thing
            // that still refuses is changing Sales Reps, because moving a rep
            // re-points the sending ACCOUNTS and a lead whose outreach has
            // started cannot follow (PUT /sales-reps has its own rule).
            campaign.status === 'paused'
              ? html`
                <button
                  class="btn btn-outline btn-sm gap-1.5 rounded-lg px-3"
                  @click=${() => sdrRouter.goTo('editCampaign', { params: { campaignId } } as any)}
                >
                  <span class="icon-[material-symbols--edit] text-base"></span>
                  Edit
                </button>
              `
              : nothing
          }
          ${
            campaign.externalCampaignId ||
            campaign.campaignType === 'email' ||
            campaign.campaignType === 'linkedin' ||
            campaign.campaignType === 'multi'
              ? html`
                <button
                  class="btn btn-outline btn-sm ${syncing ? 'btn-disabled' : ''} gap-1.5 rounded-lg px-3"
                  @click=${() => syncCampaign()}
                >
                  ${
                    syncing
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--sync] text-base"></span>`
                  }
                  Sync
                </button>
                ${
                  campaign.lastSyncedAt
                    ? html`<span class="text-base-content/40 text-xs">${formatTimeAgo(campaign.lastSyncedAt)}</span>`
                    : nothing
                }
                ${
                  campaign.status === 'active'
                    ? html`<span
                      class="text-success/70 flex items-center gap-1 text-xs"
                      title="This campaign auto-syncs every minute while open."
                    >
                      <span class="icon-[material-symbols--autorenew] text-sm"></span>Auto-sync on
                    </span>`
                    : nothing
                }
                <button
                  class="btn btn-outline btn-sm gap-1.5 rounded-lg px-3"
                  @click=${async () => {
                    try {
                      const r = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/crm/push`, {
                        method: 'POST',
                      });
                      const d = await r.json();
                      if (d.ok) {
                        toast.success(
                          `Queued ${d.contactsQueued} contacts and ${d.activitiesQueued} activities for CRM sync.`,
                        );
                      } else {
                        toast.error(d.error || 'CRM push failed');
                      }
                    } catch {
                      toast.error('CRM push request failed');
                    }
                  }}
                >
                  <span class="icon-[material-symbols--hub] text-base"></span>
                  Push to CRM
                </button>
              `
              : nothing
          }
          ${
            campaign.status === 'ready_to_launch'
              ? html`
                <button
                  class="btn btn-success btn-sm ${launching ? 'btn-disabled' : ''} gap-1.5 rounded-lg px-3"
                  @click=${launchCampaign}
                >
                  ${
                    launching
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--play-arrow] text-base"></span>`
                  }
                  Launch Campaign
                </button>
              `
              : nothing
          }
          ${
            campaign.status === 'preparing_to_launch'
              ? html`
                <button
                  class="btn btn-warning btn-sm ${launching ? 'btn-disabled' : ''} gap-1.5 rounded-lg px-3"
                  @click=${skipCooldown}
                >
                  ${
                    launching
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--fast-forward] text-base"></span>`
                  }
                  Skip Cool-Down
                </button>
              `
              : nothing
          }
          ${
            campaign.status === 'active'
              ? html`
                <button
                  class="btn btn-warning btn-sm ${
                    actionLoading === 'pause' ? 'btn-disabled' : ''
                  } gap-1.5 rounded-lg px-3"
                  @click=${() => performAction('pause')}
                >
                  ${
                    actionLoading === 'pause'
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--pause] text-base"></span>`
                  }
                  Pause
                </button>
              `
              : nothing
          }
          ${
            campaign.status === 'paused'
              ? html`
                <button
                  class="btn btn-success btn-sm ${
                    actionLoading === 'resume' ? 'btn-disabled' : ''
                  } gap-1.5 rounded-lg px-3"
                  @click=${() => performAction('resume')}
                >
                  ${
                    actionLoading === 'resume'
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--play-arrow] text-base"></span>`
                  }
                  Resume
                </button>
              `
              : nothing
          }
          <div class="bg-base-300 mx-0.5 hidden h-5 w-px sm:block"></div>
          ${
            campaign.status !== 'archived' && campaign.status !== 'draft'
              ? html`
                <button
                  class="btn btn-outline btn-sm ${
                    actionLoading === 'archive' ? 'btn-disabled' : ''
                  } gap-1.5 rounded-lg px-3"
                  title="Keeps the campaign — just moves it out of the way. Reversible."
                  @click=${() => performAction('archive')}
                >
                  ${
                    actionLoading === 'archive'
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--archive] text-base"></span>`
                  }
                  Archive
                </button>
              `
              : nothing
          }
          ${
            campaign.status === 'archived'
              ? html`
                <button
                  class="btn btn-outline btn-sm ${
                    actionLoading === 'unarchive' ? 'btn-disabled' : ''
                  } gap-1.5 rounded-lg px-3"
                  title="Take this campaign back out of the archive"
                  @click=${() => performAction('unarchive')}
                >
                  ${
                    actionLoading === 'unarchive'
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--unarchive] text-base"></span>`
                  }
                  Unarchive
                </button>
              `
              : nothing
          }
          <button
            class="btn btn-outline btn-sm ${duplicating ? 'btn-disabled' : ''} gap-1.5 rounded-lg px-3"
            @click=${duplicateCampaign}
          >
            ${
              duplicating
                ? html`<span class="loading loading-spinner loading-xs"></span>`
                : html`<span class="icon-[material-symbols--content-copy] text-base"></span>`
            }
            Duplicate
          </button>
          <button
            class="btn btn-outline btn-error btn-sm ${
              actionLoading === 'delete' ? 'btn-disabled' : ''
            } gap-1.5 rounded-lg px-3"
            title="Removes the campaign from your dashboard — restorable for 30 days"
            @click=${deleteCampaign}
          >
            ${
              actionLoading === 'delete'
                ? html`<span class="loading loading-spinner loading-xs"></span>`
                : html`<span class="icon-[material-symbols--delete-outline] text-base"></span>`
            }
            Delete
          </button>
        </div>
      </div>

      ${error ? html`<div class="alert alert-error">${error}</div>` : nothing}

      <!--
        Why the campaign isn't sending right now, if it isn't (UAT F2: informed,
        not silently blocked). Two facts the operator would otherwise have to
        guess: the sending window is closed, and/or an account has hit a limit —
        each with WHEN it resumes. Only shown for an active campaign; a paused or
        finished one is not trying to send, so "window closed" would be noise.
      -->
      ${
        campaign.status === 'active' && sendingStatus && !sendingStatus.window.open
          ? html`
            <div class="alert alert-info" role="status">
              <span class="icon-[material-symbols--schedule-outline] text-lg"></span>
              <div>
                <div class="font-semibold">Sending window reached</div>
                <div class="text-sm opacity-80">
                  No messages will be sent until
                  ${
                    sendingStatus.window.nextOpenAt
                      ? html`<span class="font-medium">${formatWhen(sendingStatus.window.nextOpenAt)}</span>`
                      : 'the next window opens'
                  }.
                </div>
              </div>
            </div>
          `
          : nothing
      }
      ${
        campaign.status === 'active' && sendingStatus && sendingStatus.limits.length > 0
          ? html`
            <div class="alert alert-info items-start" role="status">
              <span class="icon-[material-symbols--hourglass-top] mt-0.5 text-lg"></span>
              <div class="w-full">
                <div class="font-semibold">Sending limit reached — resumes automatically</div>
                <div class="mt-1 flex flex-col gap-1">
                  ${sendingStatus.limits.map(
                    l => html`
                      <div class="text-sm opacity-90">
                        <span class="badge badge-ghost badge-sm mr-1">${l.channel === 'email' ? 'Email' : 'LinkedIn'}</span>
                        <span class="font-medium">${accountNames[l.accountId] ?? l.accountId}</span>
                        — ${l.reason}${
                          l.nextSendAt
                            ? html` · <span class="font-medium">sending again ${formatWhen(l.nextSendAt)}</span>`
                            : nothing
                        }
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>
          `
          : nothing
      }
      ${
        // Dismissible once per campaign (persisted in localStorage): the skip
        // already happened and its count never changes, so after the operator
        // has seen it there is nothing to keep showing. The Do-Not-Contact
        // notice that used to sit here was removed on purpose — opted-out leads
        // now have their own stat card and the "Opted out" filter, so a standing
        // banner about them was pure noise.
        (campaign.apolloLeadsFilteredNoEmail ?? 0) > 0 && !apolloNoticeDismissed
          ? html`
            <div class="alert alert-warning items-start">
              <span class="icon-[material-symbols--filter-alt] mt-0.5 text-lg"></span>
              <span class="flex-1">
                ${campaign.apolloLeadsFilteredNoEmail} sourced Apollo
                lead${campaign.apolloLeadsFilteredNoEmail === 1 ? '' : 's'}
                ${campaign.apolloLeadsFilteredNoEmail === 1 ? 'was' : 'were'} skipped for missing contact data
                (email address / LinkedIn URL, depending on your steps) — replacements were sourced automatically
                where Apollo had them.
              </span>
              <button
                class="btn btn-ghost btn-sm"
                title="Hide this notice"
                @click=${() => {
                  setApolloNoticeDismissed(true);
                  writeApolloNoticeDismissed(campaignId);
                }}
              >
                Okay
              </button>
            </div>
          `
          : nothing
      }

      <!--
        What happens to the connection note on this campaign's connect step.
        The SAME component the wizard's sequence step renders, asked the same
        question of this campaign's own sequence — two implementations of one
        rule is how a page comes to contradict the screen the campaign was
        built on. It renders nothing unless a connect step here actually
        carries a note AND a sending account is not a paid seat.
      -->
      ${ConnectionNoteNotice({
        hasNoteOnConnectStep: (campaign.sequence ?? []).some(s => connectStepCarriesANote(s)),
        // The saved campaign's own accounts. On this page the scope is never
        // in doubt, which is what made the unscoped version so easy to miss:
        // the wizard shows the same banner one step before the answer exists.
        sendingAccountIds: campaign.linkedinAccountIds ?? [],
      })}

      <!-- Sending-account state -->
      <!--
        Shown ABOVE the job-level banner on purpose. A job problem is about one
        lead; an account problem is about every lead that account was going to
        contact, and it is the one that explains a campaign doing nothing. It
        was invisible until now, which is why "why did only four go out" took
        two days and a customer to ask.
        Only accounts that are not plainly fine appear — a list of green rows
        is a list nobody reads.
      -->
      ${(() => {
        const notFine = accountStates.filter(a => a.health !== 'ok');
        if (notFine.length === 0) return nothing;
        const attention = notFine.filter(a => a.needsAttention);
        return html`
            <div class="alert ${attention.length > 0 ? 'alert-warning' : 'alert-info'} items-start" role="status">
              <span
                class="icon-[material-symbols--${
                  attention.length > 0 ? 'account-circle-off-outline' : 'schedule-outline'
                }] mt-0.5 text-lg"
              ></span>
              <div class="w-full">
                <div class="font-semibold">
                  ${
                    attention.length > 0
                      ? html`${attention.length} sending account${attention.length === 1 ? '' : 's'} need${
                          attention.length === 1 ? 's' : ''
                        } attention.`
                      : html`${notFine.length} sending account${notFine.length === 1 ? ' is' : 's are'} limited right now.`
                  }
                </div>
                <div class="mt-2 flex flex-col gap-2">
                  ${notFine.map(
                    a => html`
                      <div class="text-sm">
                        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span class="badge badge-sm ${a.needsAttention ? 'badge-warning' : 'badge-ghost'}">
                            ${a.health.replace(/_/g, ' ')}
                          </span>
                          ${
                            accountNames[a.linkedinAccountId]
                              ? html`<span class="text-xs font-medium">${accountNames[a.linkedinAccountId]}</span>`
                              : html`<span class="font-mono text-xs opacity-70">${a.linkedinAccountId}</span>`
                          }
                          <span class="text-xs opacity-70">
                            ${a.invitesSent} sent · ${a.invitesRefused} refused · limit ${a.connectionRequestLimit}${
                              a.warmupMultiplier < 1
                                ? html` <span title="Warm-up ramp: the limit is a fraction of the full one."
                                    >(warm-up ${Math.round(a.warmupMultiplier * 100)}%)</span
                                  >`
                                : nothing
                            }
                          </span>
                        </div>
                        ${a.healthReasons.map(r => html`<div class="mt-0.5 opacity-80">${r}</div>`)}
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>
          `;
      })()}

      <!-- LinkedIn provider issues banner -->
      <!--
        Two separate things, deliberately shown differently. Throttling is the
        safety limit working and clears by itself, so it is an informational
        note; only work that is genuinely stuck gets a warning. Reporting both
        as "We're experiencing issues with LinkedIn" is what taught the operator
        to scroll past the banner — and then it hid the case that mattered.
      -->
      ${
        linkedinHealth?.hasIssues
          ? html`
            <div class="alert alert-warning" role="alert">
              <span class="icon-[material-symbols--warning-outline] text-lg"></span>
              <div>
                <div class="font-semibold">
                  ${
                    linkedinHealth.failedCount > 0
                      ? html`${linkedinHealth.failedCount} ${linkedinHealth.failedCount === 1 ? 'action' : 'actions'} could not be sent.`
                      : html`${linkedinHealth.retryingCount} ${linkedinHealth.retryingCount === 1 ? 'action is' : 'actions are'} stuck and still retrying.`
                  }
                </div>
                ${
                  (linkedinHealth.kinds ?? []).length > 0
                    ? html`
                      <ul class="text-sm opacity-80 mt-1 space-y-0.5">
                        ${(linkedinHealth.kinds ?? []).map(
                          k => html`<li>
                            <span class="font-medium">${k.count}\u00d7</span> ${k.advice}
                          </li>`,
                        )}
                      </ul>
                    `
                    : html`<div class="text-sm opacity-80">Please contact us if this persists.</div>`
                }
                ${
                  linkedinHealth.sampleError
                    ? html`<div class="text-sm opacity-80 mt-1"><span class="font-medium">Last error:</span> ${linkedinHealth.sampleError}</div>`
                    : nothing
                }
                ${
                  stuckJobs === null
                    ? html`<button class="btn btn-xs btn-ghost mt-2" ?disabled=${loadingStuck}
                        @click=${showStuckDetails}>
                        ${loadingStuck ? 'Loading…' : 'Show details'}
                      </button>`
                    : html`
                      <div class="mt-2 overflow-x-auto">
                        <table class="table table-xs">
                          <thead>
                            <tr><th>Lead</th><th>Step</th><th>Status</th><th>Tries</th><th>The provider said</th></tr>
                          </thead>
                          <tbody>
                            ${stuckJobs.map(
                              j => html`<tr>
                                <td>${j.lead ?? j.channel}</td>
                                <td>${stepLabelFor(j.channel)}</td>
                                <td>${j.status}</td>
                                <td>${j.retries}</td>
                                <td class="font-mono text-xs opacity-80">
                                  ${j.errorMessage ?? '(nothing was recorded)'}
                                </td>
                              </tr>`,
                            )}
                          </tbody>
                        </table>
                        ${
                          stuckJobs.length === 0
                            ? html`<div class="text-sm opacity-80">Nothing stuck any more — it cleared by itself.</div>`
                            : nothing
                        }
                      </div>
                    `
                }
              </div>
            </div>
          `
          : nothing
      }

      <!--
        The provider is throttling us — the limit working, not a fault.
        Worded without naming a provider: email jobs park here too, and this
        banner told an email campaign that LinkedIn was limiting it.
      -->
      ${
        !linkedinHealth?.hasIssues && (linkedinHealth?.throttledCount ?? 0) > 0
          ? html`
            <div class="alert alert-info" role="status">
              <span class="icon-[material-symbols--schedule-outline] text-lg"></span>
              <div>
                <div class="font-semibold">The provider is limiting how fast this account may send.</div>
                <div class="text-sm opacity-80">
                  ${linkedinHealth?.throttledCount ?? 0}
                  ${(linkedinHealth?.throttledCount ?? 0) === 1 ? 'action is' : 'actions are'} waiting for the limit to lift.
                  Sending continues automatically — nothing is lost and nothing needs doing.
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Generation / Ready-to-launch Banner -->
      ${
        campaign.status === 'generating'
          ? (
              () => {
                const prog = campaign.generationProgress ?? { total: 0, done: 0, failed: 0 };
                const total = prog.total || 0;
                const done = prog.done || 0;
                const failed = prog.failed || 0;
                const pct = total > 0 ? Math.round(((done + failed) / total) * 100) : 0;
                return html`
              <div class="card border-info/30 bg-info/5 border shadow-sm">
                <div class="card-body py-4">
                  <div class="flex items-center gap-3">
                    <span class="loading loading-spinner loading-md text-info"></span>
                    <div class="flex-1">
                      <h3 class="text-info font-semibold">Preparing Campaign…</h3>
                      <p class="text-base-content/60 text-xs">
                        Enriching leads and generating messages. ${done} of ${total}
                        ready${failed > 0 ? html` · ${failed} failed` : nothing}. This page refreshes automatically.
                      </p>
                    </div>
                    <span class="text-info font-bold">${pct}%</span>
                  </div>
                  <progress class="progress progress-info h-2" value=${done + failed} max=${total || 1}></progress>
                </div>
              </div>
            `;
              }
            )()
          : nothing
      }
      ${
        campaign.status === 'ready_to_launch'
          ? html`
            <div class="card border-warning/40 bg-warning/10 border shadow-sm">
              <div class="card-body flex flex-row items-center gap-3 py-4">
                <span class="icon-[material-symbols--rocket-launch] text-warning text-2xl"></span>
                <div class="flex-1">
                  <h3 class="font-semibold">Ready to Launch</h3>
                  <p class="text-base-content/70 text-sm">
                    All messages have been generated. Review and edit them per lead below. Launching starts a 1-hour
                    cool-down — nothing sends immediately, and you can keep editing until it elapses.
                  </p>
                </div>
                <button
                  class="btn btn-success btn-sm ${launching ? 'btn-disabled' : ''} gap-1.5"
                  @click=${launchCampaign}
                >
                  ${
                    launching
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--play-arrow] text-base"></span>`
                  }
                  Launch Campaign
                </button>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Launch Cool-Down Banner -->
      ${
        campaign.status === 'preparing_to_launch'
          ? html`
            <div class="card border-warning/50 bg-warning/10 border shadow-sm">
              <div class="card-body flex flex-row flex-wrap items-center gap-4 py-4">
                <span class="icon-[material-symbols--hourglass-top] text-warning text-3xl"></span>
                <div class="flex flex-1 flex-col gap-0.5">
                  <div class="flex items-center gap-2">
                    <span class="font-mono text-2xl font-bold tabular-nums">${formatCountdown(cooldownRemainingMs)}</span>
                    <span class="badge badge-warning badge-outline">One Hour Cool-Down</span>
                  </div>
                  <p class="text-base-content/70 text-sm">
                    ${
                      // "Nothing has been sent yet" is only true before the FIRST
                      // launch. On a resume it appeared over a campaign that had
                      // already sent 20 connection requests and received a
                      // reply — telling an operator they can still change
                      // anything freely, when part of the outreach is already
                      // out and cannot be taken back.
                      campaign.launchedAt
                        ? html`This campaign has already been running — outreach that went out earlier cannot be taken
                          back. You can still edit messages, leads, sequence steps, or the Sales Rep before it resumes;
                          any change restarts the hour. Skip the cool-down to resume right away.`
                        : html`Nothing has been sent yet. You can still edit messages, leads, sequence steps, or the
                          Sales Rep — any change restarts the hour. Skip the cool-down to launch right away.`
                    }
                  </p>
                </div>
                <button
                  class="btn btn-warning btn-sm ${launching ? 'btn-disabled' : ''} gap-1.5"
                  @click=${skipCooldown}
                >
                  ${
                    launching
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--fast-forward] text-base"></span>`
                  }
                  Skip
                </button>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Stats -->
      <div class="flex flex-wrap gap-4">
        ${statCard('Leads', leads.length, 'group', 'text-primary', undefined, 'all')}
        ${statCard('Pending', pendingCount, 'hourglass-empty', 'text-base-content/50', undefined, 'pending')}
        ${
          campaign.campaignType === 'linkedin' || campaign.campaignType === 'multi'
            ? html`
              ${statCard('Conn. Sent', leadCountFor('connSent'), 'person-add', 'text-info', connSentTooltip, 'connSent')}
              ${statCard('Accepted', leadCountFor('accepted'), 'how-to-reg', 'text-success', undefined, 'accepted')}
              ${
                // Its own card rather than a line in the Conn. Sent tooltip:
                // "was already connected" is a materially different outcome
                // from "accepted our invite" and you need to be able to list
                // those leads, not just count them.
                leadCountFor('alreadyConnected') > 0
                  ? statCard(
                      'Already Conn.',
                      leadCountFor('alreadyConnected'),
                      'link',
                      'text-base-content/50',
                      undefined,
                      'alreadyConnected',
                    )
                  : nothing
              }
              ${
                // Counted from the leads rather than a campaign field: these
                // buckets have no stored counter, and adding one would put the
                // same fact in two places again. `leadCountFor` runs the very
                // predicate the filter uses, so the number and the list it
                // opens cannot disagree.
                leadCountFor('invitePending') > 0
                  ? statCard(
                      'Invite Pending',
                      leadCountFor('invitePending'),
                      'hourglass-top',
                      'text-warning',
                      ['An invitation was already outstanding, so none of ours was sent'],
                      'invitePending',
                    )
                  : nothing
              }
              ${
                // The bucket that has to be visible. A lead here will never
                // move on its own: no connection, nothing outstanding, nothing
                // scheduled to change that. Eight leads sat in exactly this
                // state for three days with nothing on screen to say so.
                leadCountFor('noInvite') > 0
                  ? statCard(
                      'No Invite',
                      leadCountFor('noInvite'),
                      'link-off',
                      'text-base-content/50',
                      ['No connection and nothing outstanding — these cannot progress until an invitation goes out'],
                      'noInvite',
                    )
                  : nothing
              }
              ${statCard('DM Sent', campaign.totalDmSent ?? 0, 'send', 'text-info', undefined, 'dmSent')}
            `
            : nothing
        }
        ${
          campaign.campaignType === 'email' || campaign.campaignType === 'multi'
            ? html`
              ${statCard('Sent', totalSent, 'send', 'text-info', undefined, 'sent')}
              ${
                // Replaces the "Opened" card: on the Unipile path nothing tracks
                // opens (the mail carries no pixel, on purpose), so an open count
                // was a metric that could never move. "Opted Out" is the fact
                // that actually matters here — who asked not to be contacted —
                // and clicking it filters the list to exactly those leads.
                statCard(
                  'Opted Out',
                  leadCountFor('optedOut'),
                  'block',
                  'text-base-content/50',
                  ['On the Do Not Contact list — they asked not to be contacted, so they were not messaged'],
                  'optedOut',
                )
              }
            `
            : nothing
        }
        ${statCard('Replied', totalReplied, 'reply', 'text-accent', undefined, 'replied')}
        ${
          (campaign.totalRepliesSent ?? 0) > 0
            ? statCard(
                'Replies Sent',
                campaign.totalRepliesSent ?? 0,
                'forward',
                'text-warning',
                undefined,
                'repliesSent',
              )
            : nothing
        }
        ${
          campaign.campaignType !== 'linkedin'
            ? statCard(
                'Bounced',
                bounce.value,
                bounce.kind === 'unmeasured' ? 'help-outline' : 'error',
                bounce.kind === 'unmeasured' ? 'text-base-content/50' : 'text-error',
                bounce.tooltip.length > 0 ? bounce.tooltip : undefined,
                'bounced',
              )
            : nothing
        }
        ${
          // Waiting spans channels, so it sits outside the LinkedIn/email
          // blocks. Not an error and not progress — the third thing, which the
          // campaign had no way to express.
          leadCountFor('waiting') > 0
            ? statCard(
                'Waiting',
                leadCountFor('waiting'),
                'schedule',
                'text-base-content/50',
                ['Parked for a stated reason — outside sending hours, a daily limit, or LinkedIn blocking a re-invite'],
                'waiting',
              )
            : nothing
        }
        ${
          leadCountFor('connectionUnknown') > 0
            ? statCard(
                'State Unknown',
                leadCountFor('connectionUnknown'),
                'help-outline',
                'text-error',
                ['LinkedIn returned a value this app does not recognise — needs a look, not a guess'],
                'connectionUnknown',
              )
            : nothing
        }
        ${
          // Was "Response" = replies / EMAILS sent: always "—" on a LinkedIn
          // campaign, and the wrong ratio on a cross-channel one. Markus asked
          // what set it apart from Replied; nothing did. See reply-rate.ts.
          statCard('Reply rate', replyRate(campaign), 'percent', 'text-secondary', [
            'Leads who replied, on any channel, out of the leads contacted on any channel',
          ])
        }
      </div>

      <!-- Done Progress -->
      ${
        totalLeads > 0
          ? html`
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body flex flex-row items-center gap-4 py-3">
                <span
                  class="icon-[material-symbols--done-all] ${
                    doneRate === 100 ? 'text-success' : 'text-base-content/50'
                  } text-lg"
                ></span>
                <div class="flex flex-1 flex-col gap-1">
                  <div class="flex items-center justify-between text-sm">
                    <span class="font-medium">${totalDone} of ${totalLeads} leads done</span>
                    <span class="${doneRate === 100 ? 'text-success font-bold' : 'text-base-content/60'}"
                      >${doneRate}%</span
                    >
                  </div>
                  <progress
                    class="progress ${doneRate === 100 ? 'progress-success' : 'progress-primary'} h-2"
                    value=${totalDone}
                    max=${totalLeads}
                  ></progress>
                </div>
                ${
                  doneRate === 100 ? html`<span class="badge badge-success badge-sm">Campaign Complete</span>` : nothing
                }
              </div>
            </div>
          `
          : nothing
      }

      <!-- Offering Section (collapsible) -->
      ${
        campaign?.status !== 'draft' &&
        campaign?.offering &&
        (campaign.offering.name || campaign.offering.description || (campaign.offering.talkingPoints?.length ?? 0) > 0)
          ? html`
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body">
                <button
                  class="flex w-full items-center justify-between text-left"
                  @click=${() => setOfferingExpanded(!offeringExpanded)}
                  aria-expanded=${offeringExpanded ? 'true' : 'false'}
                >
                  <h2 class="card-title text-lg">
                    <span class="icon-[material-symbols--storefront] text-xl"></span>
                    Offering
                    ${
                      campaign.offering.name
                        ? html`<span class="text-base-content/60 text-sm font-normal">— ${campaign.offering.name}</span>`
                        : nothing
                    }
                  </h2>
                  <span
                    class="icon-[material-symbols--${
                      offeringExpanded ? 'expand-less' : 'expand-more'
                    }] text-base-content/60 text-2xl"
                  ></span>
                </button>
                ${
                  offeringExpanded
                    ? html`
                      <div class="mt-3 flex flex-col gap-4">
                        ${
                          campaign.offering.name
                            ? html`
                              <div>
                                <div class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">
                                  Product / Service
                                </div>
                                <div class="text-base font-medium">${campaign.offering.name}</div>
                              </div>
                            `
                            : nothing
                        }
                        ${
                          campaign.offering.description
                            ? html`
                              <div>
                                <div class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">
                                  Description
                                </div>
                                <p class="text-sm whitespace-pre-wrap">${campaign.offering.description}</p>
                              </div>
                            `
                            : nothing
                        }
                        ${
                          (campaign.offering.talkingPoints?.length ?? 0) > 0
                            ? html`
                              <div>
                                <div class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">
                                  Key Talking Points
                                </div>
                                <ul class="mt-1 flex flex-col gap-1">
                                  ${campaign.offering.talkingPoints!.map(
                                    p => html`
                                      <li class="flex items-start gap-2 text-sm">
                                        <span
                                          class="icon-[material-symbols--check-circle] text-success mt-0.5 shrink-0 text-base"
                                        ></span>
                                        <span>${p}</span>
                                      </li>
                                    `,
                                  )}
                                </ul>
                              </div>
                            `
                            : nothing
                        }
                        ${
                          (campaign.offering.sourceDocuments?.length ?? 0) > 0
                            ? html`
                              <div>
                                <div class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">
                                  Source Documents
                                </div>
                                <ul class="mt-1 flex flex-wrap gap-2">
                                  ${campaign.offering.sourceDocuments!.map(
                                    doc => html`
                                      <li>
                                        ${
                                          doc.url
                                            ? html`<a
                                              href=${doc.url}
                                              target="_blank"
                                              rel="noopener"
                                              class="badge badge-outline hover:badge-primary gap-1"
                                              ><span class="icon-[material-symbols--description] text-base"></span
                                              >${doc.fileName}</a
                                            >`
                                            : html`<span class="badge badge-outline gap-1"
                                              ><span class="icon-[material-symbols--description] text-base"></span
                                              >${doc.fileName}</span
                                            >`
                                        }
                                      </li>
                                    `,
                                  )}
                                </ul>
                              </div>
                            `
                            : nothing
                        }
                      </div>
                    `
                    : nothing
                }
              </div>
            </div>
          `
          : nothing
      }

      <!-- Reply Settings Section -->
      ${
        campaign?.status !== 'draft' && campaign?.autoReply
          ? html`
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body">
                <div class="flex items-center justify-between">
                  <h2 class="card-title text-lg">
                    <span class="icon-[material-symbols--reply] text-xl"></span>
                    Reply Settings
                  </h2>
                  ${
                    !editingReplySettings
                      ? html`
                        <button class="btn btn-ghost btn-sm" @click=${() => setEditingReplySettings(true)}>
                          <span class="icon-[material-symbols--edit] text-lg"></span> Edit
                        </button>
                      `
                      : nothing
                  }
                </div>

                ${
                  editingReplySettings
                    ? html`
                      <!-- Editing mode -->
                      <p class="text-base-content/60 mt-1 text-sm">
                        Configure the AI reply prompt for this campaign. The reply model is selected automatically.
                      </p>
                      <textarea
                        class="textarea textarea-bordered mt-2 w-full"
                        rows="4"
                        placeholder="Enter your reply prompt here — describe how the AI should respond to leads who reply to this campaign..."
                        .value=${replyPromptText}
                        @input=${(e: Event) => setReplyPromptText((e.target as HTMLTextAreaElement).value)}
                      ></textarea>
                      <div class="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          class="btn btn-primary btn-sm"
                          ?disabled=${savingReplyPrompt}
                          @click=${async () => {
                            await saveReplyPrompt();
                            setEditingReplySettings(false);
                          }}
                        >
                          ${
                            savingReplyPrompt
                              ? html`<span class="loading loading-spinner loading-xs"></span> Saving...`
                              : html`<span class="icon-[material-symbols--save] text-lg"></span> Save`
                          }
                        </button>
                        <button
                          class="btn btn-ghost btn-sm"
                          @click=${() => {
                            setReplyPromptText(campaign?.replyPrompt ?? '');
                            setEditingReplySettings(false);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    `
                    : html`
                      <!-- Read-only mode -->
                      <div class="mt-2 space-y-2">
                        <div>
                          <span class="text-base-content/50 text-xs font-medium">Prompt</span>
                          <p
                            class="border-base-300 bg-base-200/30 mt-1 rounded-lg border p-3 text-sm whitespace-pre-wrap"
                          >${
                            campaign?.replyPrompt ||
                            html`<span class="text-base-content/40 italic">No prompt set</span>`
                          }</p>
                        </div>
                      </div>
                      <div class="mt-3 flex flex-wrap items-center gap-2">
                        <button
                          class="btn btn-accent btn-sm"
                          ?disabled=${replyToAllSendLoading || !campaign?.replyPrompt || totalReplied === 0}
                          @click=${replyToAllAndSend}
                          title=${
                            totalReplied === 0
                              ? 'No leads have replied yet'
                              : !campaign?.replyPrompt
                                ? 'Save a reply prompt first'
                                : 'Generate AI reply drafts and send them immediately'
                          }
                        >
                          ${
                            replyToAllSendLoading
                              ? html`<span class="loading loading-spinner loading-xs"></span> Sending...`
                              : html`<span class="icon-[material-symbols--send] text-lg"></span> Reply to All & Send`
                          }
                        </button>
                        ${
                          replyToAllResult
                            ? html`<span class="text-base-content/60 text-sm">${replyToAllResult}</span>`
                            : nothing
                        }
                      </div>
                    `
                }
              </div>
            </div>
          `
          : nothing
      }

      <!-- Leads Table -->
      <div id="leads-table" class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h2 class="card-title text-lg">
                Leads
                ${
                  // With a search active, the count is the honest match summary.
                  // allLeadsLoaded is true: the leads endpoint returns every lead
                  // in one request, so the search sees the whole campaign (see
                  // lead-search.ts). Otherwise fall back to the status-filter count.
                  leadSearch.trim()
                    ? html`(${leadSearchSummary(visibleLeads.length, leads.length, { allLeadsLoaded: true })})`
                    : leadFilter === 'all'
                      ? html`(${leads.length})`
                      : html`(${visibleLeads.length} of ${leads.length})`
                }
              </h2>
              <div class="flex flex-wrap items-center gap-2">
                <button
                  class="btn btn-outline btn-sm gap-1.5"
                  title="Export every lead — with its current status and sending account — as a CSV for your CRM"
                  ?disabled=${leads.length === 0}
                  @click=${exportLeadsCsv}
                >
                  <span class="icon-[material-symbols--download] text-base"></span>
                  Export Leads
                </button>
                ${
                  campaign.status === 'paused'
                    ? html`
                      <button class="btn btn-outline btn-sm gap-1.5" @click=${() => setAddLeadsOpen(true)}>
                        <span class="icon-[material-symbols--person-add] text-base"></span>
                        Add Leads
                      </button>
                    `
                    : campaign.status === 'ready_to_launch' || campaign.status === 'preparing_to_launch'
                      ? html`
                        <button
                          class="btn btn-outline btn-sm gap-1.5"
                          title="Add or remove leads in Step 1 of the campaign editor"
                          @click=${() => sdrRouter.goTo('editCampaign', { params: { campaignId } } as any)}
                        >
                          <span class="icon-[material-symbols--edit] text-base"></span>
                          Edit Leads
                        </button>
                      `
                      : nothing
                }
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              ${
                leadFilter === 'all'
                  ? nothing
                  : html`
                    <button
                      class="badge badge-primary gap-1"
                      title="Clear filter"
                      @click=${() => setLeadFilter('all')}
                    >
                      ${leadFilterLabels[leadFilter]}
                      <span class="icon-[material-symbols--close] text-sm"></span>
                    </button>
                  `
              }
              <select
                class="select select-bordered select-sm"
                title="Filter leads by status"
                @change=${(e: Event) => setLeadFilter((e.target as HTMLSelectElement).value as LeadFilterKey)}
              >
                ${
                  // Every bucket, with its count. The stat cards are shortcuts
                  // to the common ones, but they were the ONLY way in — so a
                  // filter without a card was unreachable, and several
                  // (Sent, Opened, Done, Contacted) had no card at all. A
                  // status you can read in the table and cannot select is half
                  // a feature; this is the other half.
                  //
                  // Zero-count buckets stay listed on purpose: seeing "They
                  // invited us (0)" tells you the state exists and is empty,
                  // which is a different thing from not knowing it exists.
                  leadFilterOrder.map(key => {
                    const count = key === 'all' ? leads.length : leadCountFor(key);
                    return html`
                      <option value=${key} ?selected=${leadFilter === key}>
                        ${leadFilterLabels[key]} (${count})
                      </option>
                    `;
                  })
                }
              </select>
              <select
                class="select select-bordered select-sm"
                title="Sort leads"
                @change=${(e: Event) => setLeadSort((e.target as HTMLSelectElement).value as LeadSortKey)}
              >
                <option value="relevance" ?selected=${leadSort === 'relevance'}>Most engaged first</option>
                <option value="touchedOldest" ?selected=${leadSort === 'touchedOldest'}>
                  Last touched — oldest first
                </option>
                <option value="touchedNewest" ?selected=${leadSort === 'touchedNewest'}>
                  Last touched — newest first
                </option>
                <option value="nameAsc" ?selected=${leadSort === 'nameAsc'}>Name (A–Z)</option>
              </select>
              <label
                class="input input-bordered input-sm flex w-64 items-center gap-2 sm:w-72"
                title="Search leads by name, company or email"
              >
                <span class="icon-[material-symbols--search] text-base-content/50 text-base"></span>
                <input
                  type="search"
                  class="grow"
                  placeholder="Search name, company, email"
                  aria-label="Search leads by name, company or email"
                  .value=${leadSearch}
                  @input=${(e: Event) => setLeadSearch((e.target as HTMLInputElement).value)}
                />
              </label>
            </div>
          </div>
          ${
            leads.length === 0
              ? html`<p class="text-base-content/50 py-4 text-center">No leads in this campaign</p>`
              : visibleLeads.length === 0
                ? leadSearch.trim()
                  ? html`<div class="flex flex-col items-center gap-2 py-8 text-center">
                    <span class="icon-[material-symbols--search-off] text-base-content/30 text-4xl"></span>
                    <p class="text-base-content/60 text-sm">
                      No leads match “${leadSearch.trim()}”.
                    </p>
                    <button class="btn btn-ghost btn-xs" @click=${() => setLeadSearch('')}>Clear search</button>
                  </div>`
                  : html`<div class="flex flex-col items-center gap-2 py-8 text-center">
                    <span class="icon-[material-symbols--filter-alt-off] text-base-content/30 text-4xl"></span>
                    <p class="text-base-content/60 text-sm">
                      No leads are ${leadFilterLabels[leadFilter].toLowerCase()} yet.
                    </p>
                    <button class="btn btn-ghost btn-xs" @click=${() => setLeadFilter('all')}>Show all leads</button>
                  </div>`
                : html`
                <div class="overflow-x-auto @container">
                  <table class="table-sm table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Email</th>
                        <th>LinkedIn</th>
                        <th>Status</th>
                        <th>Step</th>
                        <th>Next Step</th>
                        <th>Last Contact</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${visibleLeads.map(l => {
                        const nextStep = getNextStepInfo(l);
                        const isExpanded = expandedLeadId === l._id;
                        return html`
                          <tr
                            class="hover ${isExpanded ? 'bg-base-200' : ''} cursor-pointer"
                            @click=${() => toggleLead(l)}
                          >
                            <td class="w-8 text-center">
                              <span
                                class="icon-[material-symbols--${
                                  isExpanded ? 'expand-less' : 'expand-more'
                                }] text-base-content/50 text-lg"
                              ></span>
                            </td>
                            <td class="font-medium">
                              <div class="flex items-center gap-1.5">
                                ${l.firstName} ${l.lastName}
                                ${
                                  l.replyText
                                    ? html`<span
                                      class="icon-[material-symbols--reply] text-success text-sm"
                                      title="Has reply"
                                    ></span>`
                                    : nothing
                                }
                              </div>
                            </td>
                            <td class="text-sm">${l.company}</td>
                            <td class="text-base-content/70 text-sm">${l.email}</td>
                            <td class="text-sm">
                              ${
                                l.linkedinUrl
                                  ? html`<a
                                    href="${l.linkedinUrl}"
                                    target="_blank"
                                    rel="noopener"
                                    class="link link-primary"
                                    >${abbreviateUrl(l.linkedinUrl)}</a
                                  >`
                                  : html`<span class="text-base-content/30">—</span>`
                              }
                            </td>
                            <td>
                              <div class="flex flex-wrap items-center gap-1">${leadStatusChipMenu(l)}</div>
                            </td>
                            <td class="text-center">${leadStepProgress(l)}</td>
                            <td class="text-sm">
                              ${
                                // "Why hasn't this happened yet" is a question
                                // about the STEP, so the waiting reason lives
                                // here rather than as a chip in the Status
                                // column — where it appeared on every row and
                                // told you nothing.
                                nextStep
                                  ? html`<span title=${l.nextStep?.waitingReason ?? ''}
                                        >${nextStep.name}${
                                          l.nextStep?.waitingReason
                                            ? html`<span
                                              class="icon-[material-symbols--schedule] text-base-content/40 ml-1 align-middle text-xs"
                                            ></span>`
                                            : nothing
                                        }</span
                                      >${
                                        nextStep.date
                                          ? html`<br /><span class="text-base-content/40 text-xs">${nextStep.date}</span>`
                                          : nothing
                                      }`
                                  : noNextStepCell(l)
                              }
                            </td>
                            <td class="text-base-content/50 text-xs">
                              ${l.lastContactedAt ? new Date(l.lastContactedAt).toLocaleDateString() : '—'}
                            </td>
                            <td class="w-8 text-right">
                              ${
                                isPreLaunchEditable(campaign.status)
                                  ? html`<button
                                    class="btn btn-ghost btn-xs btn-square text-error ${
                                      deletingLeadId === l._id ? 'btn-disabled' : ''
                                    }"
                                    title="Delete lead"
                                    @click=${(e: Event) => {
                                      e.stopPropagation();
                                      deleteLead(l);
                                    }}
                                  >
                                    ${
                                      deletingLeadId === l._id
                                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                                        : html`<span class="icon-[material-symbols--delete] text-base"></span>`
                                    }
                                  </button>`
                                  : nothing
                              }
                            </td>
                          </tr>
                          ${isExpanded ? renderLeadDetail(l) : nothing}
                        `;
                      })}
                    </tbody>
                  </table>
                </div>
              `
          }
        </div>
      </div>

      <!-- Move-to-project conflict dialog -->
      ${
        moveConflict
          ? html`
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              @click=${(e: MouseEvent) => {
                if (e.target === e.currentTarget) setMoveConflict(null);
              }}
            >
              <div class="bg-base-100 flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-2xl">
                <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--warning-outline] text-warning text-xl"></span>
                    <h3 class="font-semibold">Following leads have to be removed to move the campaign</h3>
                  </div>
                  <button class="btn btn-ghost btn-sm btn-circle" @click=${() => setMoveConflict(null)}>
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div class="overflow-y-auto p-5">
                  <p class="text-base-content/60 mb-3 text-sm">
                    These leads already exist in another campaign in this project. If the campaign can't be safely
                    edited (e.g. it's active), it will be paused instead of removing leads it has already contacted.
                  </p>
                  <div class="divide-base-300 flex flex-col divide-y">
                    ${moveConflict.leads.map(
                      l => html`
                        <div class="flex items-center justify-between gap-3 py-2">
                          <div class="min-w-0 text-sm">
                            <div class="truncate font-medium">${l.firstName} ${l.lastName}</div>
                            <div class="text-base-content/50 truncate text-xs">${l.email || l.linkedinUrl || '—'}</div>
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                </div>
                <div class="border-base-300 flex justify-end gap-2 border-t px-5 py-3">
                  <button class="btn btn-ghost btn-sm" @click=${() => setMoveConflict(null)}>Cancel</button>
                  <button
                    class="btn btn-outline btn-sm gap-1.5 ${compareBusy ? 'btn-disabled' : ''}"
                    title="Pause both campaigns and pick which side each duplicate is removed from"
                    @click=${() => void openCompareLeads(moveConflict.projectId)}
                  >
                    ${
                      compareBusy
                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                        : html`<span class="icon-[material-symbols--compare-arrows] text-base"></span>`
                    }
                    Compare Leads
                  </button>
                  <button
                    class="btn btn-warning btn-sm gap-1.5 ${movingProject ? 'btn-disabled' : ''}"
                    @click=${() =>
                      void moveToProject(
                        moveConflict.projectId,
                        moveConflict.leads.map(l => l._id),
                      )}
                  >
                    ${
                      movingProject
                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                        : html`<span class="icon-[material-symbols--delete-outline] text-base"></span>`
                    }
                    Remove
                  </button>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Compare Leads split screen (both campaigns paused server-side) -->
      ${
        compareSession
          ? (
              () => {
                const group = compareSession.groups[Math.min(comparePairIndex, compareSession.groups.length - 1)];
                if (!group) return nothing;
                /** One side of the split screen. `leadOf` picks which record of a pair this column shows. */
                const column = (
                  title: string,
                  subtitle: string,
                  leadOf: (p: { movedLead: CompareLead; siblingLead: CompareLead }) => CompareLead,
                ) => html`
                <div class="border-base-300 flex min-w-0 flex-1 flex-col rounded-lg border">
                  <div class="border-base-300 border-b px-3 py-2">
                    <div class="truncate text-sm font-semibold">${title}</div>
                    <div class="text-base-content/50 truncate text-xs">${subtitle}</div>
                  </div>
                  <div class="divide-base-300 flex flex-col divide-y">
                    ${group.pairs.map(pair => {
                      const lead = leadOf(pair);
                      const marked = pendingRemovals.has(lead._id);
                      // The pair is already resolved via the OTHER column —
                      // removing this side too would delete the person from
                      // both campaigns, which is never what "de-duplicate"
                      // means. Offer it only until one side is chosen.
                      const resolvedByOtherSide = !marked && pairResolved(pair);
                      return html`
                        <div class="flex items-center justify-between gap-2 px-3 py-2 ${marked ? 'bg-error/10' : ''}">
                          <div class="min-w-0 text-sm">
                            <div class="truncate font-medium ${marked ? 'line-through opacity-60' : ''}">
                              ${lead.firstName} ${lead.lastName}
                            </div>
                            <div class="text-base-content/50 truncate text-xs">
                              ${lead.email || lead.linkedinUrl || '—'} · ${lead.status}
                            </div>
                          </div>
                          ${
                            marked
                              ? html`<button
                                  class="btn btn-ghost btn-xs shrink-0 gap-1"
                                  @click=${() => toggleRemoval(lead._id)}
                                >
                                  <span class="icon-[material-symbols--undo] text-sm"></span>Undo
                                </button>`
                              : html`<button
                                  class="btn btn-outline btn-error btn-xs shrink-0 gap-1 ${
                                    resolvedByOtherSide ? 'btn-disabled' : ''
                                  }"
                                  title=${
                                    resolvedByOtherSide
                                      ? 'Already resolved on the other side — undo there to switch'
                                      : 'Remove this lead from this campaign'
                                  }
                                  @click=${() => toggleRemoval(lead._id)}
                                >
                                  <span class="icon-[material-symbols--delete-outline] text-sm"></span>Remove
                                </button>`
                          }
                        </div>
                      `;
                    })}
                  </div>
                </div>
              `;
                return html`
                <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <div
                    class="bg-base-100 flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl shadow-2xl"
                  >
                    <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
                      <div class="flex min-w-0 items-center gap-2">
                        <span class="icon-[material-symbols--compare-arrows] text-primary text-xl"></span>
                        <div class="min-w-0">
                          <h3 class="truncate font-semibold">Compare Leads</h3>
                          <p class="text-base-content/50 truncate text-xs">
                            Both campaigns are paused while you resolve this. Moving into "${compareSession.projectName}".
                          </p>
                        </div>
                      </div>
                      <button class="btn btn-ghost btn-sm btn-circle" @click=${() => void cancelCompareLeads()}>
                        <span class="icon-[material-symbols--close]"></span>
                      </button>
                    </div>

                    ${
                      compareSession.groups.length > 1
                        ? html`
                          <div role="tablist" class="tabs tabs-bordered px-5 pt-3">
                            ${compareSession.groups.map((g, idx) => {
                              const open = g.pairs.filter(p => !pairResolved(p)).length;
                              return html`
                                <a
                                  role="tab"
                                  class="tab gap-1.5 ${idx === comparePairIndex ? 'tab-active' : ''}"
                                  @click=${() => setComparePairIndex(idx)}
                                >
                                  ${g.siblingCampaignName}
                                  ${
                                    open > 0
                                      ? html`<span class="badge badge-warning badge-sm">${open}</span>`
                                      : html`<span class="icon-[material-symbols--check-circle] text-success text-sm"></span>`
                                  }
                                </a>
                              `;
                            })}
                          </div>
                        `
                        : nothing
                    }

                    <div class="overflow-y-auto p-5">
                      ${
                        compareError
                          ? html`<div class="alert alert-error mb-3 text-sm">
                            <span class="icon-[material-symbols--error]"></span><span>${compareError}</span>
                          </div>`
                          : nothing
                      }
                      <p class="text-base-content/60 mb-3 text-sm">
                        These ${group.pairs.length} lead${group.pairs.length === 1 ? '' : 's'} exist in both
                        campaigns. Remove each one from <em>one</em> side — whichever campaign should keep contacting
                        them stays untouched.
                      </p>
                      <div class="flex flex-col gap-3 sm:flex-row">
                        ${column(campaign.name, 'Campaign being moved', p => p.movedLead)}
                        ${column(
                          group.siblingCampaignName,
                          `Already in ${compareSession.projectName}`,
                          p => p.siblingLead,
                        )}
                      </div>
                    </div>

                    <div class="border-base-300 flex items-center justify-between gap-2 border-t px-5 py-3">
                      <span class="text-base-content/60 text-xs">
                        ${
                          unresolvedCount === 0
                            ? 'All duplicates resolved — ready to move.'
                            : `${unresolvedCount} duplicate${unresolvedCount === 1 ? '' : 's'} still to resolve`
                        }
                      </span>
                      <div class="flex gap-2">
                        <button
                          class="btn btn-ghost btn-sm ${compareBusy ? 'btn-disabled' : ''}"
                          @click=${() => void cancelCompareLeads()}
                        >
                          Cancel
                        </button>
                        <button
                          class="btn btn-primary btn-sm gap-1.5 ${
                            compareBusy || !allPairsResolved ? 'btn-disabled' : ''
                          }"
                          title=${
                            allPairsResolved
                              ? 'Remove the selected leads, move the campaign, and resume both'
                              : 'Resolve every duplicate first'
                          }
                          @click=${() => void saveCompareLeads()}
                        >
                          ${
                            compareBusy
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--save] text-base"></span>`
                          }
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              `;
              }
            )()
          : nothing
      }

      <!-- Campaign name / description edit dialog -->
      ${
        editingMeta
          ? html`
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              @click=${(e: MouseEvent) => {
                if (e.target === e.currentTarget) setEditingMeta(false);
              }}
            >
              <div class="bg-base-100 flex w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-2xl">
                <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--edit] text-primary text-xl"></span>
                    <h3 class="font-semibold">Edit Campaign</h3>
                  </div>
                  <button class="btn btn-ghost btn-sm btn-circle" @click=${() => setEditingMeta(false)}>
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div class="flex flex-col gap-3 p-5">
                  <div class="form-control">
                    <span class="label-text mb-1 text-xs font-medium">
                      Campaign Name <span class="text-error">*</span>
                    </span>
                    <input
                      class="input input-bordered input-sm w-full"
                      .value=${metaName}
                      placeholder="e.g., Q2 Enterprise Outreach"
                      @input=${(e: Event) => setMetaName((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div class="form-control">
                    <span class="label-text mb-1 text-xs font-medium">Description</span>
                    <textarea
                      class="textarea textarea-bordered textarea-sm w-full"
                      rows="4"
                      .value=${metaDescription}
                      placeholder="What is this campaign for? (internal note)"
                      @input=${(e: Event) => setMetaDescription((e.target as HTMLTextAreaElement).value)}
                    ></textarea>
                    <span class="text-base-content/50 mt-1 text-xs">
                      Internal note only — never sent to leads and not used for message generation. The text the AI
                      writes from lives in the Offering step.
                    </span>
                  </div>
                </div>
                <div class="border-base-300 flex justify-end gap-2 border-t px-5 py-3">
                  <button class="btn btn-ghost btn-sm" @click=${() => setEditingMeta(false)}>Cancel</button>
                  <button
                    class="btn btn-primary btn-sm gap-1.5 ${savingMeta || !metaName.trim() ? 'btn-disabled' : ''}"
                    @click=${() => void saveMeta()}
                  >
                    ${
                      savingMeta
                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                        : html`<span class="icon-[material-symbols--save] text-base"></span>`
                    }
                    Save
                  </button>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Add Leads modal (paused campaigns — pre-launch editing happens in the wizard's Leads step) -->
      ${AddLeadsModal({
        campaignId,
        hasApolloSearch: !!campaign.apolloSearchInputJson,
        open: addLeadsOpen,
        onClose: () => setAddLeadsOpen(false),
        onLeadsAdded: fetchData,
      })}
    </div>
  `;
});
